import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { logger } from './logger';
import { emitBackendUnavailable } from './maintenanceEvents';
import { getCurrentCorrelationId } from './correlationId';
import { API_BASE_URL } from '../config/apiConfig';
import { normalizeUtcDateString } from '../utils/dateFormat';
import type { Notification } from '../types/notification';
import type { PipelineMetricsDashboardData } from '../types/pipelineMetrics';
import type { TableMetadata, UserTablePreference } from '../types/filters';
import * as PartiesTypes from '../types/parties';
import * as AgreementsTypes from '../types/agreements';
import type {
  Incident,
  IncidentWorkspaceSummary,
  IncidentLinkedSummary,
  IncidentListResponse,
  IncidentHistory,
  IncidentSlaHistoryEntry,
  IncidentSlaRecord,
  CreateIncidentDto,
  UpdateIncidentDto,
  Comment,
  CreateCommentDto,
  Attachment,
  IncidentAttachmentPageParams,
  IncidentCiPageParams,
  IncidentCiPage,
  IncidentEpisodePageParams,
  IncidentEpisodeSummary,
  IncidentSlaPageParams,
  LoginRequest,
  LoginResponse,
  SwitchTenantResponse,
  RegisterRequest,
  RegisterResponse,
  RegistrationStatusResponse,
  AuthUser,
  UserOrganizationDto,
  ForgotPasswordResponse,
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerUser,
  CreateCustomerUserDto,
  UpdateCustomerUserDto,
  TenantBranding,
  TenantBrandingData,
  ApiError,
  IncidentStatus,
  RawAlert,
  RawAlertSummary,
  EventListResponse,
  GetEventsByIncidentParams,
  Episode,
  EpisodeListResponse,
  GetEpisodesParams,
  GetEventsParams,
  SuppressionRule,
  CreateSuppressionRuleRequest,
  UpdateSuppressionRuleRequest,
  CorrelationRule,
  CreateCorrelationRuleRequest,
  UpdateCorrelationRuleRequest,
  IngestionEndpoint,
  CreateIngestionEndpointDto,
  UpdateIngestionEndpointDto,
  RotateApiKeyDto,
  CreateIncidentFromEventsDto,
  QueueMetrics,
  DeadLetterMessage,
  DeadLetterPagedResult,
  DeadLetterOperationResult,
  DeadLetterQueueMetrics,
  ServiceBusHealthSummary,
  User,
  UserPublicProfileDto,
  UpdateUserRoleRequest,
  InviteUserRequest,
  InvitationResponse,
  ResendInvitationResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  SlaPolicy,
  HolidayCalendar,
  CreateSlaPolicyDto,
  SlaPolicyPriorityTargetDto,
  SlaPriorityTarget,
  PriorityLevelDto,
  PriorityMatrixEntryDto,
  TenantPriorityConfigDto,
  SystemPriorityTemplateDto,
  SystemSlaPolicyTemplateDto,
  UpsertSystemPriorityTemplateRequest,
  UpsertSystemReasonTemplateRequest,
  UpdateTenantPriorityConfigRequestDto,
  ApplyPriorityTemplateRequestDto,
  TenantReasonConfigDto,
  SystemReasonTemplateDto,
  CreateSystemSlaPolicyTemplateDto,
  UpdateSystemSlaPolicyTemplateDto,
  UpdateTenantReasonConfigRequestDto,
  ApplyReasonTemplateRequestDto,
  PipelineConfigurationDto,
  PipelineGraphDto,
  PipelineNodeDto,
  PipelineEdgeDto,
  UpdateValidationRulesRequest,
  SystemSettings,
  SendTestEmailRequest,
  SendTestEmailResult,
   TenantSettings,
   SlaPlatformDefaults,
   AssistantConversation,
    TenantEmailSettings,
  SendTenantTestEmailRequest,
  SendTenantTestEmailResult,
  Integration,
  RoleDto,
  PermissionDto,
  CreateRoleRequest,
  UpdateRoleRequest,
  DuplicateRoleRequest,
  AssignPermissionsRequest,
  RoleAssignment,
  AssignRoleRequest,
  PermissionCategoryGroup,
  Team,
  TeamDetailDto,
  TeamMember,
  WorkloadSummaryDto,
  CreateTeamDto,
  UpdateTeamDto,
  AddTeamMemberDto,
  SystemLogsResponse,
  SystemLogDto,
  LogLevel,
  PerformanceSummaryDto,
  SlowQueryDto,
  GroupedSlowQueryDto,
  EndpointPerformanceDto,
  TablePerformanceDto,
  PerformanceTrendBucketDto,
  ApiKeyDto,
  ApiKeyRoleDto,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  ValidationTemplateDto,
  ConfigurationItemListDto,
  CmdbDashboardDto,
  ConfigurationItemDto,
  CiLifecycleStage,
  GetConfigurationItemsParams,
  CreateConfigurationItemDto,
  UpdateConfigurationItemDto,
  CiClassDto,
  CiClassCatalogItem,
  CreateCiClassDto,
  UpdateCiClassDto,
  CiClassViewDto,
  CiClassViewDetailDto,
  CreateCiClassViewRequest,
  UpdateCiClassViewRequest,
  UpdateCiClassViewFieldsRequest,
  CiCustomFieldDto,
  CreateCiCustomFieldDto,
  UpdateCiCustomFieldDto,
  CiFieldOptionDto,
  CreateCiFieldOptionDto,
  UpdateCiFieldOptionDto,
  ReorderCiFieldOptionsDto,
  CiRelationshipDto,
  CreateCiRelationshipDto,
  CiTagDto,
  CreateCiTagDto,
  CiImpactAnalysisDto,
  CiDependencyMapDto,
  CiRelationshipTypeDto,
  CiHistoryListDto,
  CiVersionDto,
  CreateCiVersionDto,
  CiIncidentLinkDto,
  IncidentConfigurationItemDto,
  LinkCiToIncidentDto,
  UpdateIncidentCiRelationDto,
  DiscoverySourceDto,
  DiscoverySourceListDto,
  DiscoveryJobDto,
  DiscoveryJobListDto,
  CreateDiscoverySourceDto,
  UpdateDiscoverySourceDto,
  ServiceConnectionDto,
  ServiceConnectionListDto,
  CreateServiceConnectionDto,
  UpdateServiceConnectionDto,
  CiTagMappingDto,
  CreateCiTagMappingDto,
  UpdateCiTagMappingDto,
  PredefinedTagDto,
  CreatePredefinedTagDto,
  UpdatePredefinedTagDto,
  AzureHierarchyCustomerDto,
  AzureHierarchyDto,
   PipelineDefaultsListDto,
   PipelineDefaultsDto,
   AutoCloseStatus,
   ScheduledJobDto,
   TableMetadataDto,
   ReportMetrics,
   ReportDefinition,
   ReportInstance,
   ReportShareToken,
   ReportRecipient,
   AddRecipientInput,
   SendReportResponse,
   TopCis,
   IncidentVolumeTrends,
   MttrByGroup,
   SlaCompliance,
   PriorityDistribution,
   TopIncidentSources,
   ResolutionTimeDistribution,
   RecurringIncidents,
   TopReopenedIncidents,
   TopReassignedIncidents,
   FirstTimeFixRate,
  BugReportDto,
  BugReportDetailDto,
  BugReportLogEntryDto,
  DeduplicationResultDto,
  ClientErrorReport,
  FeedbackDto,
  PagedResult,
  HeaderIndicatorsDto,
  SavedNavLink,
  CreateSavedNavLinkDto,
  UpdateSavedNavLinkDto,
  SavedView,
  CreateSavedViewDto,
  UpdateSavedViewDto,
  ScriptDto,
  ScriptSummaryDto,
  CreateScriptRequest,
  UpdateScriptRequest,
  TeamsAnalyticsDto,
  TeamsEngagementDto,
  NavLinkScope,
  ResolutionSuggestionDto,
  ResolutionSuggestionFeedbackRequest,
  GetResolutionSuggestionOptions,
  IncidentAiConversationDto,
  SendIncidentAiMessageRequest,
  IncidentAiChatAllowanceDto,
  IncidentAiTechnicianAllowanceSummaryDto,
  IncidentAiChatAllowanceResetRequest,
  IncidentAiChatAllowanceResetResultDto,
  IncidentAiChatTenantSettingsDto,
  IncidentAiChatTenantSettingsUpdateRequest,
  IncidentAiChatTechnicianOverrideDto,
  IncidentAiChatTechnicianOverrideUpdateRequest,
  AiCreditBalanceDto,
  AiCreditUsageSummaryDto,
  UpdateTenantAiSettingsRequest,
  AiKnowledgeSourceDto,
  UpsertAiKnowledgeSourceRequest,
  SystemAiCreditsOverviewDto,
} from '../types';

// Re-export for backward compatibilityΓÇö canonical source is config/apiConfig.ts
export { API_BASE_URL } from '../config/apiConfig';

export interface QueuePropertiesDto {
  name: string;
  lockDuration: string;
  maxDeliveryCount: number;
  defaultMessageTimeToLive: string;
  deadLetteringOnMessageExpiration: boolean;
  maxSizeInMegabytes: number;
  status: string;
  currentSizeInBytes: number;
}

export interface TenantTokenUsageSummary {
  tenantId: string;
  tenantName: string | null;
  totalRequests: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalToolCalls: number;
}

export interface UserTokenUsageSummary {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  totalRequests: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalToolCalls: number;
}

export interface SourceUsage {
  source: string;
  requests: number;
  tokens: number;
  cost: number;
}

export interface DailyUsage {
  date: string;
  requests: number;
  tokens: number;
}

export interface AIUsageReportDto {
  totalRequests: number;
  totalTokens: number;
  totalCost: number;
  bySource: Record<string, SourceUsage>;
  byModel: Record<string, number>;
  dailyBreakdown: DailyUsage[];
}

export interface UpdateQueuePropertiesDto {
  lockDuration: string;
  maxDeliveryCount: number;
  defaultMessageTimeToLive: string;
  deadLetteringOnMessageExpiration: boolean;
}

export const OnCallRotationType = {
  Daily: 0,
  Weekly: 1,
  Custom: 2,
  Simultaneous: 3,
} as const;

export type OnCallRotationType = (typeof OnCallRotationType)[keyof typeof OnCallRotationType];

export interface OnCallParticipantDto {
  id: string;
  userId?: string | null;
  teamId?: string | null;
  type?: string | number;
  order: number;
  effectiveFrom?: string | null;
  effectiveUntil?: string | null;
}

export interface OnCallRotationDto {
  id: string;
  name: string;
  order: number;
  color?: string;
  rotationType?: OnCallRotationType | keyof typeof OnCallRotationType | string | number;
  rotationIntervalDays?: number;
  startDate?: string | null;
  endDate?: string | null;
  participants: OnCallParticipantDto[];
}

export interface OnCallOverrideDto {
  id: string;
  userId: string;
  originalUserId?: string;
  reason?: string;
  startTime: string;
  endTime: string;
}

export interface OnCallScheduleDayConfigurationDto {
  id: string;
  dayOfWeek: number;
  isActive: boolean;
  isAllDay: boolean;
  startTime: string;
  endTime: string;
}

export interface OnCallScheduleMemberRequest {
  type: 'User' | 'Team';
  userId?: string;
  teamId?: string;
  order?: number;
}

export interface OnCallScheduleDayConfigurationRequest {
  dayOfWeek: number;
  isActive: boolean;
  isAllDay: boolean;
  startTime: string;
  endTime: string;
}

export interface OnCallScheduleDto {
  id: string;
  name: string;
  description?: string;
  holidayCalendarId?: string;
  timeZone?: string;
  rotationType?: string;
  handoffTime?: string;
  handoffDay?: number;
  startDate?: string;
  isActive?: boolean;
  dayConfigurations?: OnCallScheduleDayConfigurationDto[];
  rotations?: OnCallRotationDto[];
  overrides?: OnCallOverrideDto[];
}

export interface CreateOnCallScheduleRequest {
  name: string;
  description?: string;
  timeZone?: string;
  rotationType?: 'Daily' | 'Weekly' | 'Custom' | 'Simultaneous';
  handoffTime?: string;
  handoffDay?: string | number;
  startDate?: string;
  holidayCalendarId?: string;
  dayConfigurations?: OnCallScheduleDayConfigurationRequest[];
  members?: OnCallScheduleMemberRequest[];
}

export interface UpdateOnCallScheduleRequest {
  id: string;
  name: string;
  description?: string;
  timeZone: string;
  rotationType?: 'Daily' | 'Weekly' | 'Custom' | 'Simultaneous';
  handoffTime?: string;
  handoffDay?: number;
  startDate?: string;
  dayConfigurations?: OnCallScheduleDayConfigurationRequest[];
  rotations: OnCallRotationDto[];
  overrides: OnCallOverrideDto[];
}

export interface AddOnCallRotationRequest {
  name: string;
  order: number;
  color?: string;
  rotationType?: OnCallRotationType | keyof typeof OnCallRotationType | string | number;
  rotationIntervalDays?: number;
  startDate?: string | null;
  endDate?: string | null;
}

export interface UpdateOnCallRotationRequest {
  name: string;
  order: number;
  color?: string;
  rotationType: OnCallRotationType | keyof typeof OnCallRotationType | string | number;
  rotationIntervalDays: number;
  startDate?: string | null;
  endDate?: string | null;
}

export interface AddOnCallParticipantRequest {
  userId?: string | null;
  teamId?: string | null;
  type?: 'User' | 'Team';
  effectiveFrom?: string | null;
  effectiveUntil?: string | null;
}

export interface UpdateOnCallParticipantRequest {
  effectiveFrom?: string | null;
  effectiveUntil?: string | null;
}

export interface CreateOnCallOverrideRequest {
  userId: string;
  originalUserId?: string;
  reason?: string;
  startTime: string;
  endTime: string;
}

export interface OnCallTimelineEntryDto {
  userId?: string | null;
  teamId?: string | null;
  rotationName?: string | null;
  shiftStart: string;
  shiftEnd: string;
  isOverride?: boolean;
}

export interface WhoIsOnCallEntryDto {
  scheduleId: string;
  scheduleName: string | null;
  rotationName: string | null;
  userId: string | null;
  shiftStart: string;
  shiftEnd: string;
  isOverride: boolean;
}

export interface EscalationRuleDto {
  id?: string;
  order: number;
  delayMinutes: number;
  triggerCondition: 'NotAcknowledged' | 'NotResolved' | 'NotAssigned' | 'SlaBreached';
  actionType: 'Notify' | 'Escalate' | 'CreateIncident' | 'RunAutomation' | 'AddResponders' | 'Close';
  targetType: 'Schedule' | 'User' | 'Team';
  targetId: string;
  notificationChannels: number;
  maxPriority?: string | null;
}

export interface EscalationPolicyScopeItemDto {
  id: string;
  name: string;
}

export interface EscalationPolicyDto {
  id: string;
  name: string;
  description?: string;
  applyToAllTeams: boolean;
  applyToAllCustomers: boolean;
  teams: EscalationPolicyScopeItemDto[];
  customers: EscalationPolicyScopeItemDto[];
  repeatCount: number;
  repeatIntervalMinutes: number;
  isActive?: boolean;
  rules: EscalationRuleDto[];
}

export interface NotificationPreferenceDto {
  id: string;
  notificationType: number;
  notificationTypeName: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  digestFrequency: number; // 0=Immediately, 1=Hourly, 2=Daily, 3=Weekly
  dailyDigestTime?: string; // HH:mm format
  weeklyDigestDay?: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
}

export interface UpdateNotificationPreferenceItem {
  notificationType: number;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  digestFrequency: number; // 0=Immediately, 1=Hourly, 2=Daily, 3=Weekly
  dailyDigestTime?: string; // HH:mm format
  weeklyDigestDay?: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
}

export interface UpdateNotificationPreferencesRequest {
  preferences: UpdateNotificationPreferenceItem[];
}

export interface PushSubscriptionRequest {
  endpoint: string;
  p256dhKey: string;
  authKey: string;
  userAgent?: string;
}

export interface PushSubscriptionDeleteRequest {
  endpoint: string;
}

export interface EmailLogDto {
  id: string;
  tenantId: string;
  correlationId?: string;
  customerId?: string;
  incidentId?: string;
  recipientEmail: string;
  recipientName: string;
  subject: string;
  htmlBody: string;
  fromEmail?: string;
  fromName?: string;
  status: string;
  providerStatusCode?: string;
  providerResponseBody?: string;
  sentAt: string;
  failedAt?: string;
  errorMessage?: string;
  triggerType: string;
  triggerEntityType?: string;
  triggerEntityId?: string;
  notificationType?: string;
  createdAt: string;
}

export interface TemplateVariableInfo {
  name: string;
  description: string;
  exampleValue: string;
}

export interface EmailTemplateDto {
  id: string;
  notificationType: number;
  notificationTypeName: string;
  name: string;
  subjectTemplate: string;
  bodyTemplate: string;
  availableVariables: TemplateVariableInfo[];
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface PreviewEmailTemplateResult {
  renderedSubject: string;
  renderedBodyHtml: string;
}

export interface GetEmailLogsParams {
  startDate?: string;
  endDate?: string;
  status?: string;
  triggerType?: string;
  search?: string;
  skip?: number;
  take?: number;
}

export interface CreateEscalationPolicyRequest {
  name: string;
  description?: string;
  applyToAllTeams: boolean;
  applyToAllCustomers: boolean;
  teamIds: string[];
  customerIds: string[];
  repeatCount: number;
  repeatIntervalMinutes: number;
  rules: EscalationRuleDto[];
}

export interface HolidayDto {
  id: string;
  name: string;
  date: string;
  description?: string;
  isHalfDay?: boolean;
}

export interface HolidayCalendarDto extends HolidayCalendar {
  holidays?: HolidayDto[];
}

export interface CreateHolidayCalendarRequest {
  name: string;
  description?: string;
  countryCode?: string;
}

export interface AddHolidayToCalendarRequest {
  name: string;
  date: string;
  description?: string;
  isRecurringYearly?: boolean;
  isHalfDay?: boolean;
}

function normalizeUtcDatesInPayload<T>(payload: T): T {
  if (payload === null || payload === undefined) {
    return payload;
  }

  if (typeof payload === 'string') {
    return normalizeUtcDateString(payload) as T;
  }

  if (Array.isArray(payload)) {
    for (let i = 0; i < payload.length; i += 1) {
      payload[i] = normalizeUtcDatesInPayload(payload[i]);
    }
    return payload;
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      record[key] = normalizeUtcDatesInPayload(record[key]);
    }
    return payload;
  }

  return payload;
}

function parseArrayFromJsonField<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }

  return [];
}

function normalizeTenantPriorityConfig(
  config: TenantPriorityConfigDto,
): TenantPriorityConfigDto {
  return {
    ...config,
    priorityLevels: parseArrayFromJsonField<PriorityLevelDto>(config.priorityLevels),
    impactLevels: parseArrayFromJsonField<PriorityLevelDto>(config.impactLevels),
    urgencyLevels: parseArrayFromJsonField<PriorityLevelDto>(config.urgencyLevels),
    priorityMatrix: parseArrayFromJsonField<PriorityMatrixEntryDto>(config.priorityMatrix),
  };
}

function normalizeSystemPriorityTemplate(
  template: SystemPriorityTemplateDto,
): SystemPriorityTemplateDto {
  return {
    ...template,
    priorityLevels: parseArrayFromJsonField<PriorityLevelDto>(template.priorityLevels),
    impactLevels: parseArrayFromJsonField<PriorityLevelDto>(template.impactLevels),
    urgencyLevels: parseArrayFromJsonField<PriorityLevelDto>(template.urgencyLevels),
    priorityMatrix: parseArrayFromJsonField<PriorityMatrixEntryDto>(template.priorityMatrix),
  };
}

function normalizeSystemSlaPolicyTemplate(
  template: SystemSlaPolicyTemplateDto,
): SystemSlaPolicyTemplateDto {
  const parsedPriorityTargets = parseArrayFromJsonField<SlaPolicyPriorityTargetDto | SlaPriorityTarget>(
    template.priorityTargetsJson,
  )
    .map((target) => ({
      priority: String(target?.priority ?? ''),
      firstResponseMinutes: Number(target?.firstResponseMinutes ?? 0),
      resolutionMinutes: Number(target?.resolutionMinutes ?? 0),
    }))
    .filter((target) => target.priority.length > 0);

  const businessHoursOnly = template.businessHoursOnly ?? template.useBusinessHoursOnly ?? false;

  return {
    ...template,
    businessHoursOnly,
    useBusinessHoursOnly: businessHoursOnly,
    priorityTargets: parsedPriorityTargets,
  };
}

export type BatchDeleteStreamStatus = 'started' | 'in_progress' | 'completed';

export interface BatchDeleteStreamUpdate {
  status: BatchDeleteStreamStatus;
  deleted: number;
  total: number;
}

export interface MigrationHealthStatusResponse {
  status: string;
  completedAt: string | null;
  error: string | null;
  appliedMigrationFingerprints: string[];
}

function requireIncidentRelationPage<T>(data: PagedResult<T>, relation: string): PagedResult<T> {
  if (!data || !Array.isArray(data.items) || !Number.isInteger(data.totalCount) || data.totalCount < 0) {
    throw new Error(`The server does not yet support paged incident ${relation}. Try again after the update has completed.`);
  }
  return data;
}

class ApiClient {
  private client: AxiosInstance;
  private refreshClient: AxiosInstance;
  private readonly baseUrl: string;
  private isRefreshing = false;
  private isRedirectingToLogin = false;
  private refreshQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }> = [];

  constructor() {
    this.baseUrl = API_BASE_URL;

    this.client = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.refreshClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and logging
    this.client.interceptors.request.use(
      (config) => {
        const correlationId = getCurrentCorrelationId();
        config.headers['X-Correlation-ID'] = correlationId;

        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log API request with body for PATCH/POST/PUT
        if (
          config.method?.toUpperCase() === 'PATCH' ||
          config.method?.toUpperCase() === 'POST' ||
          config.method?.toUpperCase() === 'PUT'
        ) {
          logger.logApiRequest(
            config.method?.toUpperCase() || 'GET',
            config.url || '',
            { params: config.params, body: config.data },
          );
        } else {
          logger.logApiRequest(
            config.method?.toUpperCase() || 'GET',
            config.url || '',
            { params: config.params },
          );
        }

        // Add timestamp for duration calculation (preserve existing metadata)
        config.metadata = { ...config.metadata, startTime: Date.now() };

        return config;
      },
      (error) => {
        logger.error('API request interceptor error', error);
        return Promise.reject(error);
      },
    );

    // Response interceptor for error handling and logging
    this.client.interceptors.response.use(
      (response) => {
        // Calculate request duration
        const duration = response.config.metadata?.startTime
          ? Date.now() - response.config.metadata.startTime
          : undefined;

        // Log successful response
        logger.logApiResponse(
          response.config.method?.toUpperCase() || 'GET',
          response.config.url || '',
          response.status,
          duration,
        );

        response.data = normalizeUtcDatesInPayload(response.data);

        return response;
      },
      async (error: AxiosError<ApiError>) => {
        // Suppress error handling during login redirect to prevent race conditions
        if (this.isRedirectingToLogin) {
          return Promise.reject(error);
        }

        const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
        const url = error.config?.url || 'unknown';
        const expectedStatuses = error.config?.metadata?.expectedStatuses;
        const isExpected = expectedStatuses?.includes(error.response?.status ?? 0);

        // Log API error with detailed information (skip expected status codes)
        if (!isExpected) {
          // Sanitize request body to avoid logging sensitive data (passwords, tokens, secrets)
          let sanitizedBody = error.config?.data;
          if (sanitizedBody) {
            try {
              const parsed = typeof sanitizedBody === 'string' ? JSON.parse(sanitizedBody) : sanitizedBody;
              const sensitiveKeys = ['password', 'token', 'secret', 'apiKey', 'refreshToken', 'accessToken'];
              const redacted = { ...parsed };
              for (const key of Object.keys(redacted)) {
                if (sensitiveKeys.some(s => key.toLowerCase().includes(s.toLowerCase()))) {
                  redacted[key] = '[REDACTED]';
                }
              }
              sanitizedBody = JSON.stringify(redacted);
            } catch {
              // If parsing fails, redact the entire body for safety
              sanitizedBody = '[REDACTED - unparseable]';
            }
          }

          logger.logApiError(method, url, error, {
            status: error.response?.status,
            message: error.response?.data?.message || error.message,
            requestBody: sanitizedBody,
            responseBody: error.response?.data,
          });
        }

        // Detect backend unavailability. A genuine outage looks like:
        //  • A network error with no response at all (server completely unreachable), OR
        //  • A 503 returned by a health-check endpoint (the orchestrator says we're down).
        //
        // A 503 from a feature endpoint usually means "this specific feature is
        // unconfigured" or "this dependency is temporarily unavailable", and must NOT
        // flip the whole app into maintenance mode. (See plan.md Phase 1 — this fix
        // unblocks the Teams manifest tab, which previously crashed the entire app
        // whenever MicrosoftAppId was missing.)
        const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
        const isHealthEndpoint = typeof url === 'string' && /\/health(\/|$)/.test(url);
        const is503OnHealth = error.response?.status === 503 && isHealthEndpoint;
        if (isNetworkError || is503OnHealth) {
          emitBackendUnavailable();
        }

        const originalRequest = error.config as
          | (AxiosRequestConfig & { _retry?: boolean })
          | undefined;

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          const requestUrl = originalRequest.url || '';
          const isLoginRequest = requestUrl.includes('/api/auth/login');
          const isRefreshRequest = requestUrl.includes('/api/auth/refresh');

          if (!isLoginRequest && !isRefreshRequest) {
            originalRequest._retry = true;

            if (this.isRefreshing) {
              return new Promise<string>((resolve, reject) => {
                this.refreshQueue.push({ resolve, reject });
              }).then((newToken) => {
                originalRequest.headers = originalRequest.headers ?? {};
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return this.client(originalRequest);
              });
            }

            this.isRefreshing = true;

            try {
              const newToken = await this.refreshAccessToken();
              this.processRefreshQueue(null, newToken);

              originalRequest.headers = originalRequest.headers ?? {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              return this.client(originalRequest);
            } catch (refreshError) {
              this.processRefreshQueue(refreshError);
              this.clearAuthAndRedirectToLogin();
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          }
        }

        if (error.response?.status === 401) {
          this.clearAuthAndRedirectToLogin();
        }

        return Promise.reject(error);
      },
    );
  }

  getHttpClient(): AxiosInstance {
    return this.client;
  }

  private processRefreshQueue(error: unknown, token?: string): void {
    this.refreshQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
        return;
      }

      resolve(token || '');
    });

    this.refreshQueue = [];
  }

  private clearAuthAndRedirectToLogin(): void {
    if (this.isRedirectingToLogin) {
      return;
    }
    this.isRedirectingToLogin = true;
    logger.logAuth('Unauthorized access - redirecting to login');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  }

  private async refreshAccessToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('Refresh token not found');
    }

    const response = await this.refreshClient.post<LoginResponse>(
      '/api/auth/refresh',
      { refreshToken },
    );

    localStorage.setItem('auth_token', response.data.token);
    if (response.data.refreshToken) {
      localStorage.setItem('refresh_token', response.data.refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }

    return response.data.token;
  }

  private async patch<TRequest = unknown, TResponse = void>(
    url: string,
    data: TRequest,
  ): Promise<TResponse> {
    const response = await this.client.patch<TResponse>(url, data);
    return response.data;
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    logger.logAuth('Login attempt', { email: credentials.email });
    const response = await this.client.post<LoginResponse>(
      '/api/auth/login',
      credentials,
    );
    logger.logAuth('Login successful');
    return response.data;
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    logger.logAuth('Registration attempt', {
      email: data.email,
      tenantSlug: data.tenantSlug,
    });
    const response = await this.client.post<RegisterResponse>(
      '/api/auth/register',
      data,
    );
    logger.logAuth('Registration successful', {
      tenantId: response.data.tenantId,
    });
    return response.data;
  }

  async getRegistrationStatus(): Promise<RegistrationStatusResponse> {
    const response = await this.client.get<RegistrationStatusResponse>('/api/auth/registration-status');
    return response.data;
  }

  async validateToken(): Promise<AuthUser> {
    const response = await this.client.get<AuthUser>('/api/auth/validate');
    return response.data;
  }

  async getUserOrganizations(): Promise<UserOrganizationDto[]> {
    const response = await this.client.get<UserOrganizationDto[]>('/api/users/me/organizations');
    return response.data;
  }

  async getSystemTenants(): Promise<{ id: string; name: string; slug: string; isActive: boolean }[]> {
    const response = await this.client.get<{ id: string; name: string; slug: string; isActive: boolean }[]>(
      '/api/tenants/admin/all',
    );
    return response.data;
  }

  async switchTenant(targetTenantId: string): Promise<SwitchTenantResponse> {
    const response = await this.client.post<SwitchTenantResponse>('/api/auth/switch-tenant', {
      targetTenantId,
    });
    return response.data;
  }

  async getTableMetadata(): Promise<TableMetadataDto[]> {
    const response = await this.client.get<TableMetadataDto[]>('/api/tables');
    return response.data;
  }

  // ─── Tables (universal column metadata) ────────────────────────────────
  async getAccessibleTables(): Promise<TableMetadata[]> {
    const response = await this.client.get<TableMetadata[]>('/api/tables');
    return response.data;
  }

  async getTableColumns(tableId: string): Promise<TableMetadata> {
    const response = await this.client.get<TableMetadata>(`/api/tables/${tableId}/columns`);
    return response.data;
  }

  // ─── User table preferences (per-user, per-table personalisation) ─────
  async getAllUserTablePreferences(): Promise<UserTablePreference[]> {
    const response = await this.client.get<UserTablePreference[]>('/api/user/table-preferences');
    return response.data;
  }

  async getUserTablePreference(tableId: string): Promise<UserTablePreference | null> {
    // Backend returns 200 with null body when the user hasn't saved preferences yet.
    const response = await this.client.get<UserTablePreference | null>(
      `/api/user/table-preferences/${tableId}`,
    );
    return response.data;
  }

  async upsertUserTablePreference(
    tableId: string,
    prefs: Omit<UserTablePreference, 'tableId' | 'updatedAt'>,
  ): Promise<UserTablePreference> {
    const response = await this.client.put<UserTablePreference>(
      `/api/user/table-preferences/${tableId}`,
      prefs,
    );
    return response.data;
  }

  async resetUserTablePreference(tableId: string): Promise<void> {
    await this.client.delete(`/api/user/table-preferences/${tableId}`);
  }

  async forgotPassword(data: {
    email: string;
    tenantId?: string;
  }): Promise<ForgotPasswordResponse> {
    logger.logAuth('Password reset requested', { email: data.email });
    const response = await this.client.post<ForgotPasswordResponse>(
      '/api/auth/forgot-password',
      data,
    );
    return response.data;
  }

  async resetPassword(data: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<LoginResponse> {
    logger.logAuth('Password reset attempt');
    const response = await this.client.post<LoginResponse>(
      '/api/auth/reset-password',
      data,
    );
    logger.logAuth('Password reset successful');
    return response.data;
  }

  async discoverTenant(email: string): Promise<{
    found: boolean;
    tenants?: Array<{
      tenantId: string;
      tenantName: string;
      tenantSlug: string;
      companyName?: string;
      ssoProviders: Array<{
        type: string;
        provider: string;
        enabled: boolean;
      }>;
      hasPassword: boolean;
    }>;
    // Legacy single tenant fields (for backward compatibility)
    tenantId?: string;
    tenantName?: string;
    tenantSlug?: string;
    ssoProviders?: Array<{
      type: string;
      provider: string;
      enabled: boolean;
    }>;
    hasPassword?: boolean;
    message?: string;
  }> {
    logger.logAuth('Discovering tenant(s) for email', { email });
    const response = await this.client.get('/api/auth/discover-tenant', {
      params: { email },
    });
    if (response.data.found) {
      const tenantCount = response.data.tenants?.length || 1;
      logger.logAuth(`Found ${tenantCount} tenant(s)`, {
        tenantIds: response.data.tenants?.map((t: { tenantId: string }) => t.tenantId),
      });
    } else {
      logger.logAuth('No tenants found for email');
    }
    return response.data;
  }

  // Incident endpoints
  async getIncidents(
    tenantId: string,
    params?: {
      customerId?: string;
      status?: IncidentStatus;
      excludeStatuses?: IncidentStatus[];
      assignedTeamId?: string;
      assignedToId?: string;
      impact?: number;
      urgency?: number;
      priority?: number;
      searchTerm?: string;
      sortBy?: string;
      slaBreached?: boolean;
      isMajorIncident?: boolean;
      createdFrom?: string;
      createdTo?: string;
      resolvedFrom?: string;
      resolvedTo?: string;
      closedFrom?: string;
      closedTo?: string;
      updatedFrom?: string;
      updatedTo?: string;
      primaryCiName?: string;
      minReopenCount?: number;
      minReassignmentCount?: number;
      minEventCount?: number;
      groupBy?: string;
      page?: number;
      pageSize?: number;
    },
  ): Promise<IncidentListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            queryParams.append(key, String(item));
          });
          return;
        }

        queryParams.append(key, String(value));
      });
    }

    const url = queryParams.toString().length > 0
      ? `/api/tenants/${tenantId}/incidents?${queryParams.toString()}`
      : `/api/tenants/${tenantId}/incidents`;
    const response = await this.client.get<IncidentListResponse>(url);
    return response.data;
  }

  async getReportMetrics(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
      groupBy?: string;
      useSlaBusinessHours?: boolean;
    },
  ): Promise<ReportMetrics> {
    const response = await this.client.get<ReportMetrics>(
      `/api/tenants/${tenantId}/reports/metrics`,
      { params },
    );
    return response.data;
  }

  async getReportDefinitions(tenantId: string): Promise<ReportDefinition[]> {
    const response = await this.client.get<ReportDefinition[]>(
      `/api/tenants/${tenantId}/report-register/definitions`,
    );
    return response.data;
  }

  async createReportDefinition(
    tenantId: string,
    body: {
      name: string;
      purpose: string;
      audience: string;
      dataSources: string;
      frequency: string;
      owner: string;
      calculationNotes?: string;
      format?: string;
    },
  ): Promise<ReportDefinition> {
    const response = await this.client.post<ReportDefinition>(
      `/api/tenants/${tenantId}/report-register/definitions`,
      body,
    );
    return response.data;
  }

  async approveReportDefinition(tenantId: string, definitionId: string): Promise<ReportDefinition> {
    const response = await this.client.post<ReportDefinition>(
      `/api/tenants/${tenantId}/report-register/definitions/${definitionId}/approve`,
      {},
    );
    return response.data;
  }

  async getReportInstances(tenantId: string, definitionId?: string): Promise<ReportInstance[]> {
    const response = await this.client.get<ReportInstance[]>(
      `/api/tenants/${tenantId}/report-register/instances`,
      { params: definitionId ? { definitionId } : undefined },
    );
    return response.data;
  }

  async getReportInstance(tenantId: string, instanceId: string): Promise<ReportInstance> {
    const response = await this.client.get<ReportInstance>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}`,
    );
    return response.data;
  }

  async generateReportInstance(
    tenantId: string,
    definitionId: string,
    body: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
      groupBy?: string;
      useSlaBusinessHours?: boolean;
      restatementOfId?: string;
      restatementReason?: string;
      // Phase 12 — optional scope filters that narrow the executor result set
      // (SLA/OLA/UC compliance, attribution) beyond customer/team.
      agreementIds?: string[];
      partyIds?: string[];
      customerIds?: string[];
      teamIds?: string[];
    },
  ): Promise<ReportInstance> {
    const response = await this.client.post<ReportInstance>(
      `/api/tenants/${tenantId}/report-register/definitions/${definitionId}/generate`,
      body,
    );
    return response.data;
  }

  async distributeReportInstance(
    tenantId: string,
    instanceId: string,
    distributedTo: string,
  ): Promise<ReportInstance> {
    const response = await this.client.post<ReportInstance>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/distribute`,
      { distributedTo },
    );
    return response.data;
  }

  // ------------------------- Phase 10: distribution (share tokens + recipients + send) -------------------------

  async createReportShareToken(
    tenantId: string,
    instanceId: string,
    expiresInDays?: number,
  ): Promise<ReportShareToken> {
    const response = await this.client.post<ReportShareToken>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/share-tokens`,
      { expiresInDays: expiresInDays ?? null },
    );
    return response.data;
  }

  async revokeReportShareToken(
    tenantId: string,
    instanceId: string,
    tokenId: string,
  ): Promise<ReportShareToken> {
    const response = await this.client.post<ReportShareToken>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/share-tokens/${tokenId}/revoke`,
      {},
    );
    return response.data;
  }

  async getReportShareTokens(tenantId: string, instanceId: string): Promise<ReportShareToken[]> {
    const response = await this.client.get<ReportShareToken[]>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/share-tokens`,
    );
    return response.data;
  }

  async addReportRecipients(
    tenantId: string,
    instanceId: string,
    recipients: AddRecipientInput[],
  ): Promise<ReportRecipient[]> {
    const response = await this.client.post<ReportRecipient[]>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/recipients`,
      { recipients },
    );
    return response.data;
  }

  async getReportRecipients(tenantId: string, instanceId: string): Promise<ReportRecipient[]> {
    const response = await this.client.get<ReportRecipient[]>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/recipients`,
    );
    return response.data;
  }

  async sendReportInstance(
    tenantId: string,
    instanceId: string,
    options: { createShareTokenIfMissing?: boolean; shareTokenExpiresInDays?: number | null; message?: string | null } = {},
  ): Promise<SendReportResponse> {
    const response = await this.client.post<SendReportResponse>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/send`,
      {
        createShareTokenIfMissing: options.createShareTokenIfMissing ?? true,
        shareTokenExpiresInDays: options.shareTokenExpiresInDays ?? null,
        message: options.message ?? null,
      },
    );
    return response.data;
  }

  async setReportVersionShareSafe(
    tenantId: string,
    definitionId: string,
    versionId: string,
    isShareSafe: boolean,
  ): Promise<unknown> {
    const response = await this.client.post<unknown>(
      `/api/tenants/${tenantId}/report-register/definitions/${definitionId}/versions/${versionId}/share-safe`,
      { isShareSafe },
    );
    return response.data;
  }

  /**
   * Download a frozen report instance as CSV or Excel. Returns a Blob so the caller can
   * trigger a browser save without touching the axios response headers directly.
   */
  async downloadReportInstance(
    tenantId: string,
    instanceId: string,
    format: 'csv' | 'xlsx',
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.get<Blob>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/download?format=${format}`,
      { responseType: 'blob' },
    );
    // Extract filename from Content-Disposition when present.
    const disposition = response.headers?.['content-disposition'] ?? '';
    const match = /filename="?([^";]+)"?/i.exec(String(disposition));
    const filename = match?.[1] ?? `report-${instanceId}.${format}`;
    return { blob: response.data, filename };
  }

  /** Public (anonymous) fetch of a shared report. Does NOT include auth headers. */
  async getPublicSharedReport(token: string): Promise<import('../types').PublicSharedReport> {
    // Bypass the standard interceptor by calling raw axios so we don't attach the Authorization header.
    // The base URL is inherited from this.client.
    const rawClient = this.client.defaults.baseURL ?? '';
    const response = await this.client.get<import('../types').PublicSharedReport>(
      `${rawClient.replace(/\/$/, '')}/api/public/reports/shared/${encodeURIComponent(token)}`,
      { transformRequest: [(data, headers) => { delete headers['Authorization']; delete headers['authorization']; return data; }] },
    );
    return response.data;
  }

  // -------------------------------------------------------------------------------
  // Phase 11 — in-app HTML report viewer.
  // The "render" endpoint returns a lightweight ReportInstance shape whose PayloadJson
  // holds a structured section list (Table/SummaryCards/TimeSeries/Bar/Pie) that the
  // ReportViewer component knows how to render.
  // -------------------------------------------------------------------------------

  async renderReportInstance(
    tenantId: string,
    definitionId: string,
    body: {
      periodStart: string;
      periodEnd: string;
      parameters?: Record<string, unknown>;
      cacheTtlMinutes?: number | null;
    },
  ): Promise<ReportInstance> {
    const response = await this.client.post<ReportInstance>(
      `/api/tenants/${tenantId}/report-register/definitions/${definitionId}/instances`,
      body,
    );
    return response.data;
  }

  async getReportInstancePayload(tenantId: string, instanceId: string): Promise<unknown> {
    const response = await this.client.get<unknown>(
      `/api/tenants/${tenantId}/report-register/instances/${instanceId}/payload`,
    );
    return response.data;
  }

  async getTopCis(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
    },
  ): Promise<TopCis> {
    const response = await this.client.get<TopCis>(
      `/api/tenants/${tenantId}/operational-insights/top-cis`,
      { params },
    );
    return response.data;
  }

  async getIncidentVolumeTrends(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
      groupBy?: string;
    },
  ): Promise<IncidentVolumeTrends> {
    const response = await this.client.get<IncidentVolumeTrends>(
      `/api/tenants/${tenantId}/operational-insights/incident-volume-trends`,
      { params },
    );
    return response.data;
  }

  async getMttrByGroup(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
      useSlaBusinessHours?: boolean;
    },
  ): Promise<MttrByGroup> {
    const response = await this.client.get<MttrByGroup>(
      `/api/tenants/${tenantId}/operational-insights/mttr-by-group`,
      { params },
    );
    return response.data;
  }

  async getSlaCompliance(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
    },
  ): Promise<SlaCompliance> {
    const response = await this.client.get<SlaCompliance>(
      `/api/tenants/${tenantId}/operational-insights/sla-compliance`,
      { params },
    );
    return response.data;
  }

  async getPriorityDistribution(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
    },
  ): Promise<PriorityDistribution> {
    const response = await this.client.get<PriorityDistribution>(
      `/api/tenants/${tenantId}/operational-insights/priority-distribution`,
      { params },
    );
    return response.data;
  }

  async getTopIncidentSources(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
    },
  ): Promise<TopIncidentSources> {
    const response = await this.client.get<TopIncidentSources>(
      `/api/tenants/${tenantId}/operational-insights/top-incident-sources`,
      { params },
    );
    return response.data;
  }

  async getResolutionTimeDistribution(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
    },
  ): Promise<ResolutionTimeDistribution> {
    const response = await this.client.get<ResolutionTimeDistribution>(
      `/api/tenants/${tenantId}/operational-insights/resolution-time-distribution`,
      { params },
    );
    return response.data;
  }

  async getRecurringIncidents(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
    },
  ): Promise<RecurringIncidents> {
    const response = await this.client.get<RecurringIncidents>(
      `/api/tenants/${tenantId}/operational-insights/recurring-incidents`,
      { params },
    );
    return response.data;
  }

  async getTopReopenedIncidents(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
      top?: number;
    },
  ): Promise<TopReopenedIncidents> {
    const response = await this.client.get<TopReopenedIncidents>(
      `/api/tenants/${tenantId}/operational-insights/top-reopened-incidents`,
      { params },
    );
    return response.data;
  }

  async getTopReassignedIncidents(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
      top?: number;
    },
  ): Promise<TopReassignedIncidents> {
    const response = await this.client.get<TopReassignedIncidents>(
      `/api/tenants/${tenantId}/operational-insights/top-reassigned-incidents`,
      { params },
    );
    return response.data;
  }

  async getFirstTimeFixRate(
    tenantId: string,
    params: {
      startDate: string;
      endDate: string;
      customerId?: string;
      teamId?: string;
    },
  ): Promise<FirstTimeFixRate> {
    const response = await this.client.get<FirstTimeFixRate>(
      `/api/tenants/${tenantId}/operational-insights/first-time-fix-rate`,
      { params },
    );
    return response.data;
  }

  async getIncident(tenantId: string, incidentId: string): Promise<Incident> {
    const response = await this.client.get<Incident>(
      `/api/tenants/${tenantId}/incidents/${incidentId}`,
    );
    return response.data;
  }

  async getIncidentSummary(
    tenantId: string,
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<IncidentWorkspaceSummary> {
    const response = await this.client.get<{
      incident: Incident;
      commentCount: number;
      attachmentCount: number;
      slaRecordCount: number;
      currentUserIsAssignedTeamMember: boolean;
    }>(`/api/tenants/${tenantId}/incidents/${incidentId}/summary`, { signal });
    const { incident, commentCount, attachmentCount, slaRecordCount, currentUserIsAssignedTeamMember } = response.data;
    return { ...incident, commentCount, attachmentCount, slaRecordCount, currentUserIsAssignedTeamMember };
  }

  async createIncident(
    tenantId: string,
    data: CreateIncidentDto,
  ): Promise<Incident> {
    logger.logUserAction('Create incident', { tenantId, title: data.title });
    const response = await this.client.post<Incident>(
      `/api/tenants/${tenantId}/incidents`,
      data,
    );
    logger.info('Incident created successfully', {
      incidentId: response.data.id,
    });
    return response.data;
  }

  async updateIncident(
    tenantId: string,
    incidentId: string,
    data: UpdateIncidentDto,
  ): Promise<Incident> {
    logger.logUserAction('Update incident', { tenantId, incidentId });
    const response = await this.client.patch<Incident>(
      `/api/tenants/${tenantId}/incidents/${incidentId}`,
      data,
    );
    return response.data;
  }

  async getIncidentHistory(
    tenantId: string,
    incidentId: string,
    params?: {
      changeType?: string;
      fieldName?: string;
      searchText?: string;
      fromDate?: string;
      toDate?: string;
    },
    signal?: AbortSignal,
  ): Promise<IncidentHistory[]> {
    const response = await this.client.get<IncidentHistory[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/history`,
      { params, signal },
    );
    return response.data;
  }

  async getIncidentSlaHistory(
    tenantId: string,
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<IncidentSlaHistoryEntry[]> {
    const response = await this.client.get<IncidentSlaHistoryEntry[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/sla-history`,
      { signal },
    );
    return response.data;
  }

  async getIncidentSlaRecords(
    tenantId: string,
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<IncidentSlaRecord[]> {
    const response = await this.client.get<IncidentSlaRecord[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/sla-records`,
      { signal },
    );
    return response.data;
  }

  async getIncidentSlaRecordsPage(
    tenantId: string,
    incidentId: string,
    params: IncidentSlaPageParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<IncidentSlaRecord>> {
    const response = await this.client.get<PagedResult<IncidentSlaRecord>>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/sla-records`,
      { params: { ...params, paged: true }, signal },
    );
    return requireIncidentRelationPage(response.data, 'SLA records');
  }

  async getIncidentConfigurationItems(
    tenantId: string,
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<IncidentConfigurationItemDto[]> {
    const response = await this.client.get<IncidentConfigurationItemDto[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/configuration-items`,
      { signal },
    );
    return response.data;
  }

  async getIncidentConfigurationItemsPage(
    tenantId: string,
    incidentId: string,
    params: IncidentCiPageParams,
    signal?: AbortSignal,
  ): Promise<IncidentCiPage> {
    const response = await this.client.get<IncidentCiPage>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/configuration-items`,
      { params: { ...params, paged: true }, signal },
    );
    requireIncidentRelationPage(response.data, 'configuration items');
    if (!response.data.relationCounts || Object.values(response.data.relationCounts).some((count) => !Number.isInteger(count) || count < 0)) {
      throw new Error('The server does not yet support incident configuration item totals. Try again after the update has completed.');
    }
    return response.data;
  }

  async getIncidentsByConfigurationItem(
    tenantId: string,
    incidentId: string,
    configurationItemId: string,
  ): Promise<IncidentLinkedSummary[]> {
    const response = await this.client.get<IncidentLinkedSummary[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/configuration-items/${configurationItemId}/linked-incidents`,
    );
    return response.data;
  }

  async linkCiToIncident(
    tenantId: string,
    incidentId: string,
    data: LinkCiToIncidentDto,
  ): Promise<IncidentConfigurationItemDto> {
    const response = await this.client.post<IncidentConfigurationItemDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/configuration-items`,
      data,
    );
    return response.data;
  }

  async updateIncidentCiRelation(
    tenantId: string,
    incidentId: string,
    ciId: string,
    data: UpdateIncidentCiRelationDto,
  ): Promise<IncidentConfigurationItemDto> {
    const response = await this.client.put<IncidentConfigurationItemDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/configuration-items/${ciId}`,
      data,
    );
    return response.data;
  }

  async unlinkCiFromIncident(
    tenantId: string,
    incidentId: string,
    ciId: string,
  ): Promise<void> {
    await this.client.delete(
      `/api/tenants/${tenantId}/incidents/${incidentId}/configuration-items/${ciId}`,
    );
  }

  async setIncidentPrimaryCi(
    tenantId: string,
    incidentId: string,
    configurationItemId: string,
  ): Promise<void> {
    await this.client.put(
      `/api/tenants/${tenantId}/incidents/${incidentId}/primary-ci`,
      { configurationItemId },
    );
  }

  async clearIncidentPrimaryCi(tenantId: string, incidentId: string): Promise<void> {
    await this.client.delete(
      `/api/tenants/${tenantId}/incidents/${incidentId}/primary-ci`,
    );
  }

  async setIncidentBusinessService(
    tenantId: string,
    incidentId: string,
    businessServiceId: string,
  ): Promise<void> {
    await this.client.put(
      `/api/tenants/${tenantId}/incidents/${incidentId}/business-service`,
      { businessServiceId },
    );
  }

  async clearIncidentBusinessService(tenantId: string, incidentId: string): Promise<void> {
    await this.client.delete(
      `/api/tenants/${tenantId}/incidents/${incidentId}/business-service`,
    );
  }

  // Comment endpoints
  async addComment(
    tenantId: string,
    incidentId: string,
    data: CreateCommentDto,
  ): Promise<Comment> {
    logger.logUserAction('Add comment', { tenantId, incidentId });
    const response = await this.client.post<Comment>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/comments`,
      data,
    );
    return response.data;
  }

  async getComments(tenantId: string, incidentId: string, signal?: AbortSignal): Promise<Comment[]> {
    logger.logUserAction('Get comments', { tenantId, incidentId });
    const response = await this.client.get<Comment[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/comments`,
      { signal },
    );
    return response.data;
  }

  async editComment(
    tenantId: string,
    incidentId: string,
    commentId: string,
    content: string,
  ): Promise<void> {
    logger.logUserAction('Edit comment', { tenantId, incidentId, commentId });
    await this.client.put(
      `/api/tenants/${tenantId}/incidents/${incidentId}/comments/${commentId}`,
      { content },
    );
  }

  async updateComment(
    tenantId: string,
    incidentId: string,
    commentId: string,
    content: string,
  ): Promise<void> {
    await this.editComment(tenantId, incidentId, commentId, content);
  }

  // Attachment endpoints
  async uploadAttachment(
    tenantId: string,
    incidentId: string,
    file: File,
  ): Promise<Attachment> {
    logger.logUserAction('Upload attachment', {
      tenantId,
      incidentId,
      fileName: file.name,
    });
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<Attachment>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  }

  async getAttachments(
    tenantId: string,
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<Attachment[]> {
    logger.logUserAction('Get attachments', { tenantId, incidentId });
    const response = await this.client.get<Attachment[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/attachments`,
      { signal },
    );
    return response.data;
  }

  async getIncidentAttachmentsPage(
    tenantId: string,
    incidentId: string,
    params: IncidentAttachmentPageParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<Attachment>> {
    const response = await this.client.get<PagedResult<Attachment>>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/attachments`,
      { params: { ...params, paged: true }, signal },
    );
    return requireIncidentRelationPage(response.data, 'attachments');
  }

  async deleteAttachment(
    tenantId: string,
    incidentId: string,
    attachmentId: string,
  ): Promise<void> {
    logger.logUserAction('Delete attachment', {
      tenantId,
      incidentId,
      attachmentId,
    });
    await this.client.delete(
      `/api/tenants/${tenantId}/incidents/${incidentId}/attachments/${attachmentId}`,
    );
  }

  async getAttachmentDownloadUrl(
    tenantId: string,
    incidentId: string,
    attachmentId: string,
  ): Promise<{ downloadUrl: string }> {
    logger.logUserAction('Get attachment download URL', {
      tenantId,
      incidentId,
      attachmentId,
    });
    const response = await this.client.get<{ downloadUrl: string }>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/attachments/${attachmentId}/download-url`,
    );
    return response.data;
  }

  // Customer endpoints
  async getCustomers(
    tenantId: string,
    params?: { isActive?: boolean; searchTerm?: string },
    signal?: AbortSignal,
  ): Promise<Customer[]> {
    const response = await this.client.get<Customer[]>(
      `/api/tenants/${tenantId}/customers`,
      { params, signal },
    );
    return response.data;
  }

  async getCustomer(tenantId: string, customerId: string): Promise<Customer> {
    const response = await this.client.get<Customer>(
      `/api/tenants/${tenantId}/customers/${customerId}`,
    );
    return response.data;
  }

  async createCustomer(
    tenantId: string,
    data: CreateCustomerDto,
  ): Promise<Customer> {
    logger.logUserAction('Create customer', { tenantId, name: data.name });
    const response = await this.client.post<Customer>(
      `/api/tenants/${tenantId}/customers`,
      data,
    );
    logger.info('Customer created successfully', {
      customerId: response.data.id,
    });
    return response.data;
  }

  async updateCustomer(
    tenantId: string,
    customerId: string,
    data: UpdateCustomerDto,
  ): Promise<Customer> {
    logger.logUserAction('Update customer', { tenantId, customerId });
    const response = await this.client.put<Customer>(
      `/api/tenants/${tenantId}/customers/${customerId}`,
      data,
    );
    logger.info('Customer updated successfully', {
      customerId: response.data.id,
    });
    return response.data;
  }

  async deleteCustomer(tenantId: string, customerId: string): Promise<void> {
    logger.logUserAction('Delete customer', { tenantId, customerId });
    await this.client.delete(
      `/api/tenants/${tenantId}/customers/${customerId}`,
    );
    logger.info('Customer deleted successfully', { customerId });
  }

  // Discovery endpoints
  async getDiscoverySources(
    tenantId: string,
    params?: {
      customerId?: string;
      searchTerm?: string;
      page?: number;
      pageSize?: number;
    },
  ): Promise<DiscoverySourceListDto> {
    const response = await this.client.get<DiscoverySourceListDto>(
      `/api/tenants/${tenantId}/discovery-sources`,
      { params },
    );
    return response.data;
  }

  async getDiscoverySource(tenantId: string, id: string): Promise<DiscoverySourceDto> {
    const response = await this.client.get<DiscoverySourceDto>(
      `/api/tenants/${tenantId}/discovery-sources/${id}`,
    );
    return response.data;
  }

  async createDiscoverySource(
    tenantId: string,
    customerId: string,
    data: CreateDiscoverySourceDto,
  ): Promise<DiscoverySourceDto> {
    const response = await this.client.post<DiscoverySourceDto>(
      `/api/tenants/${tenantId}/customers/${customerId}/discovery-sources`,
      data,
    );
    return response.data;
  }

  async updateDiscoverySource(
    tenantId: string,
    id: string,
    data: UpdateDiscoverySourceDto,
  ): Promise<DiscoverySourceDto> {
    const response = await this.client.put<DiscoverySourceDto>(
      `/api/tenants/${tenantId}/discovery-sources/${id}`,
      data,
    );
    return response.data;
  }

  async deleteDiscoverySource(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/discovery-sources/${id}`);
  }

  // ============================================================================
  // Service Connections
  // ============================================================================

  async getServiceConnections(
    tenantId: string,
    customerId: string,
  ): Promise<ServiceConnectionListDto> {
    const response = await this.client.get<ServiceConnectionListDto>(
      `/api/tenants/${tenantId}/customers/${customerId}/service-connections`,
    );
    return response.data;
  }

  async getServiceConnection(
    tenantId: string,
    customerId: string,
    id: string,
  ): Promise<ServiceConnectionDto> {
    const response = await this.client.get<ServiceConnectionDto>(
      `/api/tenants/${tenantId}/customers/${customerId}/service-connections/${id}`,
    );
    return response.data;
  }

  async createServiceConnection(
    tenantId: string,
    customerId: string,
    data: CreateServiceConnectionDto,
  ): Promise<ServiceConnectionDto> {
    const response = await this.client.post<ServiceConnectionDto>(
      `/api/tenants/${tenantId}/customers/${customerId}/service-connections`,
      data,
    );
    return response.data;
  }

  async updateServiceConnection(
    tenantId: string,
    customerId: string,
    id: string,
    data: UpdateServiceConnectionDto,
  ): Promise<ServiceConnectionDto> {
    const response = await this.client.put<ServiceConnectionDto>(
      `/api/tenants/${tenantId}/customers/${customerId}/service-connections/${id}`,
      data,
    );
    return response.data;
  }

  async deleteServiceConnection(
    tenantId: string,
    customerId: string,
    id: string,
  ): Promise<void> {
    await this.client.delete(
      `/api/tenants/${tenantId}/customers/${customerId}/service-connections/${id}`,
    );
  }

  async triggerDiscoveryJob(tenantId: string, id: string): Promise<void> {
    await this.client.post(`/api/tenants/${tenantId}/discovery-sources/${id}/trigger`);
  }

  async getDiscoveryJobs(
    tenantId: string,
    sourceId: string,
    params?: {
      page?: number;
      pageSize?: number;
    },
  ): Promise<DiscoveryJobListDto> {
    const response = await this.client.get<DiscoveryJobListDto>(
      `/api/tenants/${tenantId}/discovery-sources/${sourceId}/jobs`,
      { params },
    );
    return response.data;
  }

  async getAllDiscoveryJobs(
    tenantId: string,
    params: {
      pageNumber?: number;
      pageSize?: number;
      sourceId?: string;
      status?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      sortBy?: string;
      sortOrder?: string;
    } = {},
  ): Promise<{ items: DiscoveryJobDto[]; totalCount: number; pageNumber: number; pageSize: number; totalPages: number }> {
    const queryParams = new URLSearchParams();
    const appendIfDefined = (key: string, value?: number | string) => {
      if (value === undefined || value === null) {
        return;
      }

      if (typeof value === 'string' && value.trim().length === 0) {
        return;
      }

      queryParams.append(key, String(value));
    };

    appendIfDefined('pageNumber', params.pageNumber);
    appendIfDefined('pageSize', params.pageSize);
    appendIfDefined('sourceId', params.sourceId);
    appendIfDefined('status', params.status);
    appendIfDefined('dateFrom', params.dateFrom);
    appendIfDefined('dateTo', params.dateTo);
    appendIfDefined('search', params.search);
    appendIfDefined('sortBy', params.sortBy);
    appendIfDefined('sortOrder', params.sortOrder);

    const query = queryParams.toString();
    const path = query
      ? `/api/tenants/${tenantId}/discovery-jobs?${query}`
      : `/api/tenants/${tenantId}/discovery-jobs`;
    const response = await this.client.get<{ items: DiscoveryJobDto[]; totalCount: number; pageNumber: number; pageSize: number; totalPages: number }>(
      path,
    );
    return response.data;
  }

  async getDiscoveryJob(
    tenantId: string,
    sourceId: string,
    jobId: string,
  ): Promise<DiscoveryJobDto> {
    const response = await this.client.get<DiscoveryJobDto>(
      `/api/tenants/${tenantId}/discovery-sources/${sourceId}/jobs/${jobId}`,
    );
    return response.data;
  }

  async getDiscoveryJobLogs(
    tenantId: string,
    sourceId: string,
    jobId: string,
  ): Promise<SystemLogDto[]> {
    const response = await this.client.get<SystemLogDto[]>(
      `/api/tenants/${tenantId}/discovery-sources/${sourceId}/jobs/${jobId}/logs`,
    );
    return response.data;
  }

  async cancelDiscoveryJob(
    tenantId: string,
    sourceId: string,
    jobId: string,
    reason?: string,
    forceCancel?: boolean,
  ): Promise<{ success: boolean; message: string; jobStatus: string }> {
    const response = await this.client.post<{ success: boolean; message: string; jobStatus: string }>(
      `/api/tenants/${tenantId}/discovery-sources/${sourceId}/jobs/${jobId}/cancel`,
      { reason, forceCancel },
    );
    return response.data;
  }

  // CMDB endpoints
  async getConfigurationItems(
    tenantId: string,
    params?: GetConfigurationItemsParams,
    signal?: AbortSignal,
  ): Promise<ConfigurationItemListDto> {
    const normalizedParams =
      params && typeof params === 'object'
        ? (() => {
            const mutableParams = {
              ...(params as GetConfigurationItemsParams & { filters?: unknown; sortBy?: string }),
            };

            if (mutableParams.filters && typeof mutableParams.filters !== 'string') {
              try {
                mutableParams.filters = JSON.stringify(mutableParams.filters);
              } catch (error) {
                logger.warn('Failed to stringify configuration item filters', {
                  error,
                  tenantId,
                });
                delete mutableParams.filters;
              }
            }

            return mutableParams;
          })()
        : params;

    const response = await this.client.get<ConfigurationItemListDto>(
      `/api/tenants/${tenantId}/configuration-items`,
      { params: normalizedParams, signal },
    );
    return response.data;
  }

  async getCmdbDashboard(tenantId: string, customerId?: string): Promise<CmdbDashboardDto> {
    const response = await this.client.get<CmdbDashboardDto>(
      `/api/tenants/${tenantId}/configuration-items/dashboard`,
      { params: customerId ? { customerId } : undefined },
    );
    return response.data;
  }

  async getAzureHierarchyCustomers(tenantId: string): Promise<AzureHierarchyCustomerDto[]> {
    const response = await this.client.get<AzureHierarchyCustomerDto[]>(
      `/api/tenants/${tenantId}/configuration-items/azure-hierarchy/customers`,
    );
    return response.data;
  }

  async getAzureHierarchy(tenantId: string, customerId?: string): Promise<AzureHierarchyDto> {
    const response = await this.client.get<AzureHierarchyDto>(
      `/api/tenants/${tenantId}/configuration-items/azure-hierarchy`,
      { params: customerId ? { customerId } : undefined },
    );
    return response.data;
  }

  async getConfigurationItem(tenantId: string, id: string, signal?: AbortSignal): Promise<ConfigurationItemDto> {
    const response = await this.client.get<ConfigurationItemDto>(
      `/api/tenants/${tenantId}/configuration-items/${id}`,
      { signal },
    );
    return response.data;
  }

  async createConfigurationItem(
    tenantId: string,
    data: CreateConfigurationItemDto,
    customerId?: string,
  ): Promise<ConfigurationItemDto> {
    const url = customerId
      ? `/api/tenants/${tenantId}/customers/${customerId}/configuration-items`
      : `/api/tenants/${tenantId}/configuration-items`;
    const response = await this.client.post<ConfigurationItemDto>(url, data);
    return response.data;
  }

  async updateConfigurationItem(
    tenantId: string,
    id: string,
    data: UpdateConfigurationItemDto,
  ): Promise<ConfigurationItemDto> {
    const response = await this.client.put<ConfigurationItemDto>(
      `/api/tenants/${tenantId}/configuration-items/${id}`,
      data,
    );
    return response.data;
  }

  async updateConfigurationItemLifecycleStage(
    tenantId: string,
    id: string,
    lifecycleStage: CiLifecycleStage,
  ): Promise<ConfigurationItemDto> {
    const response = await this.client.put<ConfigurationItemDto>(
      `/api/tenants/${tenantId}/configuration-items/${id}/lifecycle-stage`,
      { lifecycleStage },
    );
    return response.data;
  }

  async deleteConfigurationItem(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/configuration-items/${id}`);
  }

  async restoreConfigurationItem(tenantId: string, id: string): Promise<ConfigurationItemDto> {
    const response = await this.client.post<ConfigurationItemDto>(
      `/api/tenants/${tenantId}/configuration-items/${id}/restore`,
    );
    return response.data;
  }

  async retireCi(tenantId: string, ciId: string, reason?: string): Promise<void> {
    await this.client.post(
      `/api/tenants/${tenantId}/configuration-items/${ciId}/retire`,
      { reason },
    );
  }

  async unretireCi(tenantId: string, ciId: string, reason?: string): Promise<void> {
    await this.client.post(
      `/api/tenants/${tenantId}/configuration-items/${ciId}/unretire`,
      { reason },
    );
  }

  async getCiClasses(tenantId: string): Promise<CiClassDto[]> {
    const response = await this.client.get<CiClassDto[]>(`/api/tenants/${tenantId}/ci-classes`);
    return response.data;
  }

  // CMDB Catalog
  async getCiClassCatalog(tenantId: string): Promise<CiClassCatalogItem[]> {
    const response = await this.client.get<CiClassCatalogItem[]>(
      `/api/tenants/${tenantId}/cmdb/catalog`,
    );
    return response.data;
  }

  async subscribeToCiClass(tenantId: string, classId: string): Promise<void> {
    await this.client.post(`/api/tenants/${tenantId}/cmdb/catalog/${classId}/subscribe`);
  }

  async unsubscribeFromCiClass(tenantId: string, classId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/cmdb/catalog/${classId}/unsubscribe`);
  }

  async createCiClass(tenantId: string, data: CreateCiClassDto): Promise<CiClassDto> {
    const response = await this.client.post<CiClassDto>(
      `/api/tenants/${tenantId}/ci-classes`,
      data,
    );
    return response.data;
  }

  async updateCiClass(
    tenantId: string,
    classId: string,
    data: UpdateCiClassDto,
  ): Promise<CiClassDto> {
    const response = await this.client.put<CiClassDto>(
      `/api/tenants/${tenantId}/ci-classes/${classId}`,
      data,
    );
    return response.data;
  }

  async deleteCiClass(tenantId: string, classId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/ci-classes/${classId}`);
  }

  // CI Class Views
  async getCiClassViews(tenantId: string, classId: string): Promise<CiClassViewDto[]> {
    const response = await this.client.get<CiClassViewDto[]>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/views`,
    );
    return response.data;
  }

  async getCiClassView(
    tenantId: string,
    classId: string,
    viewId: string,
  ): Promise<CiClassViewDetailDto> {
    const response = await this.client.get<CiClassViewDetailDto>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/views/${viewId}`,
    );
    return response.data;
  }

  async createCiClassView(
    tenantId: string,
    classId: string,
    data: CreateCiClassViewRequest,
  ): Promise<CiClassViewDto> {
    const response = await this.client.post<CiClassViewDto>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/views`,
      data,
    );
    return response.data;
  }

  async updateCiClassView(
    tenantId: string,
    classId: string,
    viewId: string,
    data: UpdateCiClassViewRequest,
  ): Promise<CiClassViewDto> {
    const response = await this.client.put<CiClassViewDto>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/views/${viewId}`,
      data,
    );
    return response.data;
  }

  async deleteCiClassView(tenantId: string, classId: string, viewId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/ci-classes/${classId}/views/${viewId}`);
  }

  async setSystemDefaultView(classId: string, viewId: string): Promise<void> {
    await this.client.put(`/api/ci-classes/${classId}/views/${viewId}/set-system-default`);
  }

  async setTenantDefaultView(tenantId: string, classId: string, viewId: string): Promise<void> {
    await this.client.put(
      `/api/tenants/${tenantId}/ci-classes/${classId}/views/${viewId}/set-tenant-default`,
    );
  }

  async resetTenantDefaultView(tenantId: string, classId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/ci-classes/${classId}/views/tenant-default`);
  }

  async getEffectiveDefaultView(tenantId: string, classId: string): Promise<CiClassViewDto | null> {
    // Backend returns 200 with null body when no default view is configured — fallback to built-in.
    const response = await this.client.get<CiClassViewDto | null>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/views/effective-default`,
    );
    return response.data;
  }

  async updateCiClassViewFields(
    tenantId: string,
    classId: string,
    viewId: string,
    data: UpdateCiClassViewFieldsRequest,
  ): Promise<CiClassViewDetailDto> {
    const response = await this.client.put<CiClassViewDetailDto>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/views/${viewId}/fields`,
      data,
    );
    return response.data;
  }

  async getCiCustomFields(
    tenantId: string,
    classId: string,
    includeInherited?: boolean,
  ): Promise<CiCustomFieldDto[]> {
    const querySuffix = includeInherited ? '?includeInherited=true' : '';
    const response = await this.client.get<CiCustomFieldDto[]>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/custom-fields${querySuffix}`,
    );
    return response.data;
  }

  async createCiCustomField(
    tenantId: string,
    classId: string,
    data: CreateCiCustomFieldDto,
  ): Promise<CiCustomFieldDto> {
    const response = await this.client.post<CiCustomFieldDto>(
      `/api/tenants/${tenantId}/ci-classes/${classId}/custom-fields`,
      data,
    );
    return response.data;
  }

  async updateCiCustomField(
    tenantId: string,
    fieldId: string,
    data: UpdateCiCustomFieldDto,
  ): Promise<CiCustomFieldDto> {
    const response = await this.client.put<CiCustomFieldDto>(
      `/api/tenants/${tenantId}/custom-fields/${fieldId}`,
      data,
    );
    return response.data;
  }

  async deleteCiCustomField(tenantId: string, fieldId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/custom-fields/${fieldId}`);
  }

  async getCiFieldOptions(tenantId: string, fieldId: string): Promise<CiFieldOptionDto[]> {
    const response = await this.client.get<CiFieldOptionDto[]>(
      `/api/tenants/${tenantId}/custom-fields/${fieldId}/options`,
    );
    return response.data;
  }

  async createCiFieldOption(
    tenantId: string,
    fieldId: string,
    data: CreateCiFieldOptionDto,
  ): Promise<CiFieldOptionDto> {
    const response = await this.client.post<CiFieldOptionDto>(
      `/api/tenants/${tenantId}/custom-fields/${fieldId}/options`,
      data,
    );
    return response.data;
  }

  async updateCiFieldOption(
    tenantId: string,
    id: string,
    data: UpdateCiFieldOptionDto,
  ): Promise<CiFieldOptionDto> {
    const response = await this.client.put<CiFieldOptionDto>(
      `/api/tenants/${tenantId}/field-options/${id}`,
      data,
    );
    return response.data;
  }

  async deleteCiFieldOption(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/field-options/${id}`);
  }

  async activateCiFieldOption(tenantId: string, id: string): Promise<CiFieldOptionDto> {
    const response = await this.client.post<CiFieldOptionDto>(
      `/api/tenants/${tenantId}/field-options/${id}/activate`,
    );
    return response.data;
  }

  async deactivateCiFieldOption(tenantId: string, id: string): Promise<CiFieldOptionDto> {
    const response = await this.client.post<CiFieldOptionDto>(
      `/api/tenants/${tenantId}/field-options/${id}/deactivate`,
    );
    return response.data;
  }

  async reorderCiFieldOptions(
    tenantId: string,
    fieldId: string,
    data: ReorderCiFieldOptionsDto,
  ): Promise<void> {
    await this.client.post(`/api/tenants/${tenantId}/custom-fields/${fieldId}/options/reorder`, data);
  }

  async getConfigurationItemRelationships(
    tenantId: string,
    id: string,
  ): Promise<CiRelationshipDto[]> {
    const response = await this.client.get<CiRelationshipDto[]>(
      `/api/tenants/${tenantId}/configuration-items/${id}/relationships`,
    );
    return response.data;
  }

  async getCiRelationships(
    tenantId: string,
    params?: { sourceCiId?: string; targetCiId?: string },
  ): Promise<CiRelationshipDto[]> {
    const response = await this.client.get<CiRelationshipDto[]>(
      `/api/tenants/${tenantId}/ci-relationships`,
      { params },
    );
    return response.data;
  }

  async createCiRelationship(
    tenantId: string,
    data: CreateCiRelationshipDto,
  ): Promise<CiRelationshipDto> {
    const response = await this.client.post<CiRelationshipDto>(
      `/api/tenants/${tenantId}/ci-relationships`,
      data,
    );
    return response.data;
  }

  async deleteCiRelationship(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/ci-relationships/${id}`);
  }

  // CI Tags
  async getCiTags(tenantId: string, ciId: string): Promise<CiTagDto[]> {
    const response = await this.client.get<CiTagDto[]>(
      `/api/tenants/${tenantId}/configuration-items/${ciId}/tags`,
    );
    return response.data;
  }

  async updateCiTags(
    tenantId: string,
    ciId: string,
    tags: CreateCiTagDto[],
  ): Promise<CiTagDto[]> {
    const response = await this.client.put<CiTagDto[]>(
      `/api/tenants/${tenantId}/configuration-items/${ciId}/tags`,
      tags,
    );
    return response.data;
  }

  async deleteCiTag(tenantId: string, ciId: string, tagId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/configuration-items/${ciId}/tags/${tagId}`);
  }

  // CI Tag Mappings
  async getCiTagMappings(tenantId: string): Promise<CiTagMappingDto[]> {
    const response = await this.client.get<CiTagMappingDto[]>(
      `/api/tenants/${tenantId}/tag-mappings`,
    );
    return response.data;
  }

  async createCiTagMapping(
    tenantId: string,
    data: CreateCiTagMappingDto,
  ): Promise<CiTagMappingDto> {
    const response = await this.client.post<CiTagMappingDto>(
      `/api/tenants/${tenantId}/tag-mappings`,
      data,
    );
    return response.data;
  }

  async updateCiTagMapping(
    tenantId: string,
    id: string,
    data: UpdateCiTagMappingDto,
  ): Promise<CiTagMappingDto> {
    const response = await this.client.put<CiTagMappingDto>(
      `/api/tenants/${tenantId}/tag-mappings/${id}`,
      data,
    );
    return response.data;
  }

  async deleteCiTagMapping(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/tag-mappings/${id}`);
  }

  // Predefined Tags
  async getPredefinedTags(tenantId: string): Promise<PredefinedTagDto[]> {
    const response = await this.client.get<PredefinedTagDto[]>(
      `/api/tenants/${tenantId}/predefined-tags`,
    );
    return response.data;
  }

  async getPredefinedTag(tenantId: string, id: string): Promise<PredefinedTagDto> {
    const response = await this.client.get<PredefinedTagDto>(
      `/api/tenants/${tenantId}/predefined-tags/${id}`,
    );
    return response.data;
  }

  async createPredefinedTag(
    tenantId: string,
    data: CreatePredefinedTagDto,
  ): Promise<PredefinedTagDto> {
    const response = await this.client.post<PredefinedTagDto>(
      `/api/tenants/${tenantId}/predefined-tags`,
      data,
    );
    return response.data;
  }

  async updatePredefinedTag(
    tenantId: string,
    id: string,
    data: UpdatePredefinedTagDto,
  ): Promise<PredefinedTagDto> {
    const response = await this.client.put<PredefinedTagDto>(
      `/api/tenants/${tenantId}/predefined-tags/${id}`,
      data,
    );
    return response.data;
  }

  async deletePredefinedTag(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/predefined-tags/${id}`);
  }

  async getCiImpactAnalysis(
    tenantId: string,
    ciId: string,
    maxDepth: number = 3,
  ): Promise<CiImpactAnalysisDto> {
    const response = await this.client.get<CiImpactAnalysisDto>(
      `/api/tenants/${tenantId}/configuration-items/${ciId}/impact-analysis`,
      {
        params: { maxDepth },
      },
    );
    return response.data;
  }

  async getCiDependencyMap(
    tenantId: string,
    ciId: string,
    maxDepth: number = 3,
  ): Promise<CiDependencyMapDto> {
    const response = await this.client.get<CiDependencyMapDto>(
      `/api/tenants/${tenantId}/configuration-items/${ciId}/dependency-map`,
      {
        params: { maxDepth },
      },
    );
    return response.data;
  }

  async getCiRelationshipTypes(tenantId: string): Promise<CiRelationshipTypeDto[]> {
    const response = await this.client.get<CiRelationshipTypeDto[]>(
      `/api/tenants/${tenantId}/ci-relationship-types`,
    );
    return response.data;
  }

  async getConfigurationItemHistory(
    tenantId: string,
    id: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<CiHistoryListDto> {
    const response = await this.client.get<CiHistoryListDto>(
      `/api/tenants/${tenantId}/configuration-items/${id}/history`,
      { params },
    );
    return response.data;
  }

  async getConfigurationItemVersions(tenantId: string, id: string): Promise<CiVersionDto[]> {
    const response = await this.client.get<CiVersionDto[]>(
      `/api/tenants/${tenantId}/configuration-items/${id}/versions`,
    );
    return response.data;
  }

  async createConfigurationItemVersion(
    tenantId: string,
    id: string,
    data: CreateCiVersionDto,
  ): Promise<CiVersionDto> {
    const response = await this.client.post<CiVersionDto>(
      `/api/tenants/${tenantId}/configuration-items/${id}/versions`,
      data,
    );
    return response.data;
  }

  async getConfigurationItemIncidents(tenantId: string, id: string): Promise<CiIncidentLinkDto[]> {
    const response = await this.client.get<CiIncidentLinkDto[]>(
      `/api/tenants/${tenantId}/configuration-items/${id}/incidents`,
    );
    return response.data;
  }

  // Customer Users Management
  async getCustomerUsers(
    tenantId: string,
    customerId: string,
    signal?: AbortSignal,
  ): Promise<CustomerUser[]> {
    logger.info('Getting customer users', { tenantId, customerId });
    const response = await this.client.get<CustomerUser[]>(
      `/api/tenants/${tenantId}/customers/${customerId}/users`,
      { signal },
    );
    return response.data;
  }

  async getCustomerUser(
    tenantId: string,
    customerId: string,
    customerUserId: string,
  ): Promise<CustomerUser> {
    logger.info('Getting customer user', { tenantId, customerId, customerUserId });
    const response = await this.client.get<CustomerUser>(
      `/api/tenants/${tenantId}/customers/${customerId}/users/${customerUserId}`,
    );
    return response.data;
  }

  async createCustomerUser(
    tenantId: string,
    customerId: string,
    data: CreateCustomerUserDto,
  ): Promise<CustomerUser> {
    logger.logUserAction('Create customer user', { tenantId, customerId });
    const response = await this.client.post<CustomerUser>(
      `/api/tenants/${tenantId}/customers/${customerId}/users`,
      data,
    );
    logger.info('Customer user created successfully', {
      customerUserId: response.data.id,
    });
    return response.data;
  }

  async updateCustomerUser(
    tenantId: string,
    customerId: string,
    customerUserId: string,
    data: UpdateCustomerUserDto,
  ): Promise<CustomerUser> {
    logger.logUserAction('Update customer user', { tenantId, customerId, customerUserId });
    const response = await this.client.put<CustomerUser>(
      `/api/tenants/${tenantId}/customers/${customerId}/users/${customerUserId}`,
      data,
    );
    logger.info('Customer user updated successfully', {
      customerUserId: response.data.id,
    });
    return response.data;
  }

  async deleteCustomerUser(
    tenantId: string,
    customerId: string,
    customerUserId: string,
  ): Promise<void> {
    logger.logUserAction('Delete customer user', { tenantId, customerId, customerUserId });
    await this.client.delete(
      `/api/tenants/${tenantId}/customers/${customerId}/users/${customerUserId}`,
    );
    logger.info('Customer user deleted successfully', { customerUserId });
  }

  async getTenantBranding(tenantId: string): Promise<TenantBranding | null> {
    logger.logUserAction('Get tenant branding', { tenantId });
    try {
      const response = await this.client.get<TenantBranding | null>(
        `/api/tenants/${tenantId}/branding`,
      );
      logger.info('Tenant branding retrieved successfully', { tenantId });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const statusCode = error.response?.status;
        if (statusCode === 404 || statusCode === 500) {
          logger.warn('Tenant branding unavailable. Falling back to default branding.', {
            tenantId,
            statusCode,
          });
          return null;
        }
      }

      this.handleApiError(error, 'Failed to load tenant branding');
    }
  }

  async updateTenantBranding(
    tenantId: string,
    branding: TenantBrandingData,
  ): Promise<TenantBranding> {
    logger.logUserAction('Update tenant branding', { tenantId });
    const response = await this.client.put<TenantBranding>(
      `/api/tenants/${tenantId}/branding`,
      branding,
    );
    logger.info('Tenant branding updated successfully', { tenantId });
    return response.data;
  }

  async uploadTenantBrandingLogo(
    tenantId: string,
    file: File,
  ): Promise<{ logoUrl: string }> {
    logger.logUserAction('Upload tenant branding logo', {
      tenantId,
      fileName: file.name,
      fileSize: file.size,
    });

    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<{ logoUrl: string }>(
      `/api/tenants/${tenantId}/branding/logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    logger.info('Tenant branding logo uploaded successfully', { tenantId });
    return response.data;
  }

  // Event Management endpoints
  async getEvents(
    tenantId: string,
    params: GetEventsParams = {},
  ): Promise<EventListResponse> {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) {
      queryParams.append('skip', params.skip.toString());
    }
    if (params.take !== undefined) {
      queryParams.append('take', params.take.toString());
    }
    if (params.receivedFrom) {
      queryParams.append('receivedFrom', params.receivedFrom);
    }
    if (params.receivedTo) {
      queryParams.append('receivedTo', params.receivedTo);
    }
    if (params.customerId) {
      queryParams.append('customerId', params.customerId);
    }
    if (params.endpointId) {
      queryParams.append('endpointId', params.endpointId);
    }
    if (params.isProcessed !== undefined) {
      queryParams.append('isProcessed', params.isProcessed.toString());
    }
    if (params.isDuplicate !== undefined) {
      queryParams.append('isDuplicate', params.isDuplicate.toString());
    }
    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      queryParams.append('sortDirection', params.sortDirection);
    }
    if (params.includeCounts !== undefined) {
      queryParams.append('includeCounts', params.includeCounts.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/tenants/${tenantId}/events?${queryString}`
      : `/api/tenants/${tenantId}/events`;

    const response = await this.client.get<EventListResponse>(
      url,
    );
    return response.data;
  }

  async getEvent(tenantId: string, eventId: string, signal?: AbortSignal): Promise<RawAlert> {
    const response = await this.client.get<RawAlert>(
      `/api/tenants/${tenantId}/events/${eventId}`,
      { signal },
    );
    return response.data;
  }

  async getEpisodes(
    tenantId: string,
    params: GetEpisodesParams = {},
  ): Promise<EpisodeListResponse> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.correlationType) queryParams.append('correlationType', params.correlationType);
    if (params.isEscalated !== undefined) queryParams.append('isEscalated', params.isEscalated.toString());
    if (params.incidentId) queryParams.append('incidentId', params.incidentId);
    if (params.mainResourceIdContains) queryParams.append('mainResourceIdContains', params.mainResourceIdContains);
    if (params.customerId) queryParams.append('customerId', params.customerId);
    if (params.sourceEquals) queryParams.append('sourceEquals', params.sourceEquals);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.slaStatus) queryParams.append('slaStatus', params.slaStatus);
    if (params.correlationRuleId) queryParams.append('correlationRuleId', params.correlationRuleId);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.firstAlertAtFrom) queryParams.append('firstAlertAtFrom', params.firstAlertAtFrom);
    if (params.firstAlertAtTo) queryParams.append('firstAlertAtTo', params.firstAlertAtTo);
    if (params.lastAlertAtFrom) queryParams.append('lastAlertAtFrom', params.lastAlertAtFrom);
    if (params.lastAlertAtTo) queryParams.append('lastAlertAtTo', params.lastAlertAtTo);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/tenants/${tenantId}/episodes?${queryString}`
      : `/api/tenants/${tenantId}/episodes`;

    const response = await this.client.get<EpisodeListResponse>(url);
    return response.data;
  }

  async getEpisodesByIncident(
    tenantId: string,
    incidentId: string,
    signal?: AbortSignal,
  ): Promise<Episode[]> {
    const response = await this.client.get<Episode[]>(
      `/api/tenants/${tenantId}/events/by-incident/${incidentId}/episodes`,
      { signal },
    );
    return response.data;
  }

  async getIncidentEpisodesPage(
    tenantId: string,
    incidentId: string,
    params: IncidentEpisodePageParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<IncidentEpisodeSummary>> {
    const response = await this.client.get<PagedResult<IncidentEpisodeSummary>>(
      `/api/tenants/${tenantId}/events/by-incident/${incidentId}/episodes`,
      { params: { ...params, paged: true }, signal },
    );
    return requireIncidentRelationPage(response.data, 'episodes');
  }

  async getEpisode(tenantId: string, episodeId: string): Promise<Episode> {
    const response = await this.client.get<Episode>(`/api/tenants/${tenantId}/episodes/${episodeId}`);
    return response.data;
  }

  async getScripts(
    tenantId: string,
    customerId?: string,
  ): Promise<ScriptSummaryDto[]> {
    const response = await this.client.get<ScriptSummaryDto[]>(
      `/api/tenants/${tenantId}/scripts`,
      { params: { customerId } },
    );
    return response.data;
  }

  async getScriptById(tenantId: string, scriptId: string): Promise<ScriptDto> {
    const response = await this.client.get<ScriptDto>(
      `/api/tenants/${tenantId}/scripts/${scriptId}`,
    );
    return response.data;
  }

  async createScript(
    tenantId: string,
    data: CreateScriptRequest,
  ): Promise<ScriptDto> {
    const response = await this.client.post<ScriptDto>(
      `/api/tenants/${tenantId}/scripts`,
      data,
    );
    return response.data;
  }

  async updateScript(
    tenantId: string,
    scriptId: string,
    data: UpdateScriptRequest,
  ): Promise<ScriptDto> {
    const response = await this.client.put<ScriptDto>(
      `/api/tenants/${tenantId}/scripts/${scriptId}`,
      data,
    );
    return response.data;
  }

  async deleteScript(tenantId: string, scriptId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/scripts/${scriptId}`);
  }

  async getSystemScripts(): Promise<ScriptSummaryDto[]> {
    const response = await this.client.get<ScriptSummaryDto[]>('/api/system/scripts');
    return response.data;
  }

  async createSystemScript(data: CreateScriptRequest): Promise<ScriptDto> {
    const response = await this.client.post<ScriptDto>('/api/system/scripts', data);
    return response.data;
  }

  async updateSystemScript(
    scriptId: string,
    data: UpdateScriptRequest,
  ): Promise<ScriptDto> {
    const response = await this.client.put<ScriptDto>(
      `/api/system/scripts/${scriptId}`,
      data,
    );
    return response.data;
  }

  async deleteSystemScript(scriptId: string): Promise<void> {
    await this.client.delete(`/api/system/scripts/${scriptId}`);
  }

  async getSuppressionRules(
    tenantId: string,
    params?: { isEnabled?: boolean; suppressionType?: string },
  ): Promise<SuppressionRule[]> {
    const response = await this.client.get<SuppressionRule[]>(
      `/api/tenants/${tenantId}/suppression-rules`,
      { params },
    );
    return response.data;
  }

  async getSuppressionRule(tenantId: string, id: string): Promise<SuppressionRule> {
    const response = await this.client.get<SuppressionRule>(
      `/api/tenants/${tenantId}/suppression-rules/${id}`,
    );
    return response.data;
  }

  async createSuppressionRule(
    tenantId: string,
    data: CreateSuppressionRuleRequest,
  ): Promise<SuppressionRule> {
    const response = await this.client.post<SuppressionRule>(
      `/api/tenants/${tenantId}/suppression-rules`,
      data,
    );
    return response.data;
  }

  async updateSuppressionRule(
    tenantId: string,
    id: string,
    data: UpdateSuppressionRuleRequest,
  ): Promise<SuppressionRule> {
    const response = await this.client.put<SuppressionRule>(
      `/api/tenants/${tenantId}/suppression-rules/${id}`,
      data,
    );
    return response.data;
  }

  async deleteSuppressionRule(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/suppression-rules/${id}`);
  }

  async getCorrelationRules(
    tenantId: string,
    params?: { isEnabled?: boolean; correlationType?: string },
  ): Promise<CorrelationRule[]> {
    const response = await this.client.get<CorrelationRule[]>(
      `/api/tenants/${tenantId}/correlation-rules`,
      { params },
    );
    return response.data;
  }

  async getCorrelationRule(tenantId: string, id: string): Promise<CorrelationRule> {
    const response = await this.client.get<CorrelationRule>(
      `/api/tenants/${tenantId}/correlation-rules/${id}`,
    );
    return response.data;
  }

  async createCorrelationRule(
    tenantId: string,
    data: CreateCorrelationRuleRequest,
  ): Promise<CorrelationRule> {
    const response = await this.client.post<CorrelationRule>(
      `/api/tenants/${tenantId}/correlation-rules`,
      data,
    );
    return response.data;
  }

  async updateCorrelationRule(
    tenantId: string,
    id: string,
    data: UpdateCorrelationRuleRequest,
  ): Promise<CorrelationRule> {
    const response = await this.client.put<CorrelationRule>(
      `/api/tenants/${tenantId}/correlation-rules/${id}`,
      data,
    );
    return response.data;
  }

  async deleteCorrelationRule(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/correlation-rules/${id}`);
  }

  async getEventsByIncident(
    tenantId: string,
    incidentId: string,
    params: GetEventsByIncidentParams = {},
    signal?: AbortSignal,
  ): Promise<PagedResult<RawAlertSummary>> {
    const queryParams = new URLSearchParams({ paged: 'true' });
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.set(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    const url = `/api/tenants/${tenantId}/events/by-incident/${incidentId}${queryString ? `?${queryString}` : ''}`;
    const response = await this.client.get<PagedResult<RawAlertSummary>>(url, { signal });
    if (!response.data || !Array.isArray(response.data.items)
      || !Number.isInteger(response.data.totalCount) || response.data.totalCount < 0) {
      throw new Error('The server does not yet support paged incident events. Try again after the update has completed.');
    }
    return response.data;
  }

  async createIncidentFromEvents(
    tenantId: string,
    data: CreateIncidentFromEventsDto,
  ): Promise<Incident> {
    logger.logUserAction('Create incident from events', {
      tenantId,
      eventCount: data.eventIds.length,
    });
    const response = await this.client.post<Incident>(
      `/api/tenants/${tenantId}/events/create-incident`,
      data,
    );
    logger.info('Incident created from events', {
      incidentId: response.data.id,
    });
    return response.data;
  }

  async reprocessEvent(tenantId: string, eventId: string): Promise<void> {
    logger.logUserAction('Reprocess event', { tenantId, eventId });
    await this.client.post(`/api/tenants/${tenantId}/events/${eventId}/reprocess`);
    logger.info('Event queued for reprocessing', { eventId });
  }

  async previewTruncateEvents(params: {
    tenantId?: string;
    statusFilter?: number;
    olderThan?: string;
  }): Promise<{ count: number }> {
    logger.logUserAction('Preview truncate events', params);
    const response = await this.client.post<{ count: number }>(
      '/api/system/events/truncate/preview',
      params,
    );
    return response.data;
  }

  async truncateEvents(params: {
    tenantId?: string;
    statusFilter?: number;
    olderThan?: string;
  }): Promise<{ deletedCount: number }> {
    logger.logUserAction('Truncate events', params);
    const response = await this.client.post<{ deletedCount: number }>(
      '/api/system/events/truncate',
      params,
    );
    return response.data;
  }

  async truncateEventsStreaming(
    params: {
      tenantId?: string;
      statusFilter?: number;
      olderThan?: string;
    },
    onProgress: (progress: BatchDeleteStreamUpdate) => void,
  ): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${this.baseUrl}/api/system/events/truncate/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Truncation failed: ${response.status} ${response.statusText}`);
    }

    await this.readNdjsonStream<BatchDeleteStreamUpdate>(response, onProgress);
  }

  async getPipelineMetrics(
    tenantId: string,
    params?: {
      maxExecutions?: number;
      since?: string;
    },
  ): Promise<PipelineMetricsDashboardData> {
    const response = await this.client.get<PipelineMetricsDashboardData>(
      `/api/tenants/${tenantId}/events/pipeline-metrics`,
      { params },
    );
    return response.data;
  }

  // Ingestion Endpoint Management endpoints
  async getIngestionEndpoints(
    tenantId: string,
    params?: { isActive?: boolean; customerId?: string },
  ): Promise<IngestionEndpoint[]> {
    const response = await this.client.get<IngestionEndpoint[]>(
      `/api/tenants/${tenantId}/ingestion-endpoints`,
      { params },
    );
    return response.data;
  }

  async getIngestionEndpoint(
    tenantId: string,
    endpointId: string,
  ): Promise<IngestionEndpoint> {
    const response = await this.client.get<IngestionEndpoint>(
      `/api/tenants/${tenantId}/ingestion-endpoints/${endpointId}`,
    );
    return response.data;
  }

  async createIngestionEndpoint(
    tenantId: string,
    data: CreateIngestionEndpointDto,
  ): Promise<IngestionEndpoint> {
    logger.logUserAction('Create ingestion endpoint', {
      tenantId,
      name: data.name,
    });
    const response = await this.client.post<IngestionEndpoint>(
      `/api/tenants/${tenantId}/ingestion-endpoints`,
      data,
    );
    logger.info('Ingestion endpoint created', { endpointId: response.data.id });
    return response.data;
  }

  async updateIngestionEndpoint(
    tenantId: string,
    endpointId: string,
    data: UpdateIngestionEndpointDto,
  ): Promise<IngestionEndpoint> {
    logger.logUserAction('Update ingestion endpoint', { tenantId, endpointId });
    const response = await this.client.put<IngestionEndpoint>(
      `/api/tenants/${tenantId}/ingestion-endpoints/${endpointId}`,
      data,
    );
    logger.info('Ingestion endpoint updated', { endpointId: response.data.id });
    return response.data;
  }

  async rotateApiKey(
    tenantId: string,
    endpointId: string,
    data?: RotateApiKeyDto,
  ): Promise<IngestionEndpoint> {
    logger.logUserAction('Rotate API key', { tenantId, endpointId });
    const response = await this.client.post<IngestionEndpoint>(
      `/api/tenants/${tenantId}/ingestion-endpoints/${endpointId}/rotate-key`,
      data || {},
    );
    logger.info('API key rotated', { endpointId });
    return response.data;
  }

  async deleteIngestionEndpoint(
    tenantId: string,
    endpointId: string,
  ): Promise<void> {
    logger.logUserAction('Delete ingestion endpoint', { tenantId, endpointId });
    await this.client.delete(
      `/api/tenants/${tenantId}/ingestion-endpoints/${endpointId}`,
    );
    logger.info('Ingestion endpoint deleted', { endpointId });
  }

  // ============ Service Bus Monitoring ============

  async getServiceBusHealth(): Promise<ServiceBusHealthSummary> {
    logger.logUserAction('Get Service Bus health summary');
    const response = await this.client.get<ServiceBusHealthSummary>(
      '/api/service-bus/health',
    );
    return response.data;
  }

  async getAllQueueMetrics(): Promise<QueueMetrics[]> {
    logger.logUserAction('Get all queue metrics');
    const response = await this.client.get<QueueMetrics[]>(
      '/api/service-bus/queues',
    );
    return response.data;
  }

  async getQueueMetrics(queueName: string): Promise<QueueMetrics> {
    logger.logUserAction('Get queue metrics', { queueName });
    const response = await this.client.get<QueueMetrics>(
      `/api/service-bus/queues/${queueName}`,
    );
    return response.data;
  }

  async getQueueProperties(queueName: string): Promise<QueuePropertiesDto> {
    logger.logUserAction('Get queue properties', { queueName });
    const response = await this.client.get<QueuePropertiesDto>(
      `/api/service-bus/queues/${queueName}/properties`,
    );
    return response.data;
  }

  async updateQueueProperties(
    queueName: string,
    properties: UpdateQueuePropertiesDto,
  ): Promise<QueuePropertiesDto> {
    logger.logUserAction('Update queue properties', { queueName, properties });
    const response = await this.client.put<QueuePropertiesDto>(
      `/api/service-bus/queues/${queueName}/properties`,
      properties,
    );
    return response.data;
  }

  async getDeadLetterQueueMetrics(
    queueName: string,
  ): Promise<DeadLetterQueueMetrics> {
    logger.logUserAction('Get dead letter queue metrics', { queueName });
    const response = await this.client.get<DeadLetterQueueMetrics>(
      `/api/service-bus/queues/${queueName}/dead-letter`,
    );
    return response.data;
  }

  async getDeadLetterMetrics(queueName: string): Promise<DeadLetterQueueMetrics> {
    return this.getDeadLetterQueueMetrics(queueName);
  }

  async getDeadLetterMessages(
    queueName: string,
    page: number = 1,
    pageSize: number = 25,
  ): Promise<DeadLetterPagedResult> {
    logger.logUserAction('Get dead letter messages', { queueName, page, pageSize });
    const response = await this.client.get<DeadLetterPagedResult>(
      `/api/service-bus/queues/${queueName}/dead-letter/messages`,
      { params: { page, pageSize } },
    );
    return response.data;
  }

  async getDeadLetterMessageDetail(
    queueName: string,
    sequenceNumber: number,
  ): Promise<DeadLetterMessage> {
    logger.logUserAction('Get dead letter message detail', {
      queueName,
      sequenceNumber,
    });
    const response = await this.client.get<DeadLetterMessage>(
      `/api/service-bus/queues/${queueName}/dead-letter/messages/${sequenceNumber}`,
    );
    return response.data;
  }

  async resubmitDeadLetterMessages(
    queueName: string,
    sequenceNumbers: number[],
    reviewedCompletionIndeterminate = false,
  ): Promise<void> {
    logger.logUserAction('Resubmit dead letter messages', {
      queueName,
      sequenceNumbers,
    });
    await this.client.post(
      `/api/service-bus/queues/${queueName}/dead-letter/resubmit`,
      { sequenceNumbers, reviewedCompletionIndeterminate },
    );
  }

  async purgeDeadLetterQueue(queueName: string): Promise<void> {
    logger.logUserAction('Purge dead letter queue', { queueName });
    await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/purge`);
  }

  async resubmitAllDeadLetterMessages(queueName: string): Promise<void> {
    logger.logUserAction('Resubmit all dead letter messages', { queueName });
    await this.client.post(`/api/service-bus/queues/${queueName}/dead-letter/resubmit-all`);
  }

  async resubmitDeadLetterMessageWithEdit(
    queueName: string,
    sequenceNumber: number,
    modifiedBody: string,
    reviewedCompletionIndeterminate = false,
  ): Promise<void> {
    logger.logUserAction('Resubmit dead letter message with edit', {
      queueName,
      sequenceNumber,
    });
    await this.client.post(
      `/api/service-bus/queues/${queueName}/dead-letter/resubmit-with-edit`,
      { sequenceNumber, modifiedBody, reviewedCompletionIndeterminate },
    );
  }

  async deleteDeadLetterMessages(
    queueName: string,
    sequenceNumbers: number[],
  ): Promise<DeadLetterOperationResult> {
    logger.logUserAction('Delete dead letter messages', {
      queueName,
      sequenceNumbers,
    });
    const response = await this.client.post<DeadLetterOperationResult>(
      `/api/service-bus/queues/${queueName}/dead-letter/delete`,
      { sequenceNumbers },
      { timeout: 120000 }, // 2 minute timeout for DLQ operations
    );
    return response.data;
  }

  // User Management endpoints
  async getUsers(
    tenantId: string,
    includeInvitations: boolean = true,
    includeInactive: boolean = false,
    signal?: AbortSignal,
  ): Promise<User[]> {
    logger.logUserAction('Get users', { tenantId });
    const response = await this.client.get<User[]>(
      `/api/tenants/${tenantId}/users`,
      { params: { includeInvitations, includeInactive }, signal },
    );
    return response.data;
  }

  async getUserPublicProfile(
    tenantId: string,
    userId: string,
  ): Promise<UserPublicProfileDto> {
    logger.logUserAction('Get user public profile', { tenantId, userId });
    const response = await this.client.get<UserPublicProfileDto>(
      `/api/tenants/${tenantId}/users/${userId}/profile`,
    );
    return response.data;
  }

  async updateUserRole(
    tenantId: string,
    userId: string,
    data: UpdateUserRoleRequest,
  ): Promise<User> {
    logger.logUserAction('Update user role', {
      tenantId,
      userId,
      role: data.role,
    });
    const response = await this.client.put<User>(
      `/api/tenants/${tenantId}/users/${userId}/role`,
      data,
    );
    logger.info('User role updated successfully', {
      userId,
      newRole: data.role,
    });
    return response.data;
  }

  async inviteUser(
    tenantId: string,
    data: InviteUserRequest,
  ): Promise<InvitationResponse> {
    logger.logUserAction('Invite user', { tenantId, email: data.email });
    const response = await this.client.post<InvitationResponse>(
      `/api/tenants/${tenantId}/invitations`,
      data,
    );
    logger.info('User invited successfully', {
      invitationId: response.data.invitationId,
    });
    return response.data;
  }

  async acceptInvitation(
    data: AcceptInvitationRequest,
  ): Promise<AcceptInvitationResponse> {
    logger.info('Accepting invitation', { token: data.token.substring(0, 10) + '...' });
    const response = await this.client.post<AcceptInvitationResponse>(
      '/api/invitations/accept',
      data,
    );
    logger.info('Invitation accepted successfully', {
      userId: response.data.userId,
    });
    return response.data;
  }

  async resendInvitation(
    tenantId: string,
    invitationId: string,
  ): Promise<ResendInvitationResponse> {
    logger.logUserAction('Resend invitation', { tenantId, invitationId });
    const response = await this.client.post<ResendInvitationResponse>(
      `/api/tenants/${tenantId}/invitations/${invitationId}/resend`,
    );
    logger.info('Invitation resent successfully', {
      invitationId: response.data.invitationId,
      emailSent: response.data.emailSent,
    });
    return response.data;
  }

  async deleteUser(tenantId: string, userId: string): Promise<void> {
    logger.logUserAction('Delete user', { tenantId, userId });
    await this.client.delete(`/api/tenants/${tenantId}/users/${userId}`);
    logger.info('User deleted successfully', { userId });
  }

  async deactivateUser(
    tenantId: string,
    userId: string,
    data: { reassignToUserId?: string; anonymizeData: boolean },
  ): Promise<void> {
    logger.logUserAction('Deactivate user', { tenantId, userId, anonymizeData: data.anonymizeData });
    await this.client.post(`/api/users/${userId}/deactivate`, data);
    logger.info('User deactivated successfully', { userId });
  }

  async reactivateUser(tenantId: string, userId: string): Promise<void> {
    logger.logUserAction('Reactivate user', { tenantId, userId });
    await this.client.post(`/api/users/${userId}/reactivate`);
    logger.info('User reactivated successfully', { userId });
  }

  // SLA Policy endpoints
  async getSlaPolicies(tenantId: string): Promise<SlaPolicy[]> {
    logger.logUserAction('Get SLA policies', { tenantId });
    const response = await this.client.get<SlaPolicy[]>(
      `/api/tenants/${tenantId}/sla-policies`,
    );
    return response.data;
  }

  async getBugReports(
    tenantId: string,
    filters?: { isAutoGenerated?: boolean },
  ): Promise<PagedResult<BugReportDto>> {
    logger.logUserAction('Get bug reports', { tenantId, ...filters });

    const queryParams = new URLSearchParams();
    queryParams.append('pageSize', '10000');

    if (typeof filters?.isAutoGenerated === 'boolean') {
      queryParams.append('isAutoGenerated', String(filters.isAutoGenerated));
    }

    const response = await this.client.get<PagedResult<BugReportDto>>(
      `/api/tenants/${tenantId}/bug-reports?${queryParams.toString()}`,
    );
    return response.data;
  }

  async getBugReportsPaginated(
    tenantId: string,
    page: number,
    pageSize: number,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc',
  ): Promise<{ items: BugReportDto[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    logger.logUserAction('Get paginated bug reports', { tenantId, page, pageSize, sortBy, sortDirection });

    const queryParams = new URLSearchParams();
    queryParams.append('page', String(page));
    queryParams.append('pageSize', String(pageSize));

    if (sortBy && sortBy.trim().length > 0) {
      queryParams.append('sortBy', sortBy);
    }

    if (sortDirection && sortDirection.trim().length > 0) {
      queryParams.append('sortDirection', sortDirection);
    }

    const response = await this.client.get<PagedResult<BugReportDto>>(
      `/api/tenants/${tenantId}/bug-reports?${queryParams.toString()}`,
    );

    return {
      items: response.data.items ?? [],
      totalCount: response.data.totalCount ?? 0,
      page: response.data.pageNumber ?? page,
      pageSize: response.data.pageSize ?? pageSize,
      totalPages: response.data.totalPages ?? 0,
    };
  }

  async getBugReportsCount(tenantId: string, filters?: { isAutoGenerated?: boolean }): Promise<number> {
    logger.logUserAction('Get bug report count', { tenantId, ...filters });

    const queryParams = new URLSearchParams();
    if (typeof filters?.isAutoGenerated === 'boolean') {
      queryParams.append('isAutoGenerated', String(filters.isAutoGenerated));
    }

    const url = queryParams.toString().length > 0
      ? `/api/tenants/${tenantId}/bug-reports/count?${queryParams.toString()}`
      : `/api/tenants/${tenantId}/bug-reports/count`;

    const response = await this.client.get<number>(url);
    return response.data;
  }

  async getBugReport(tenantId: string, bugReportId: string): Promise<BugReportDetailDto> {
    const response = await this.client.get<BugReportDetailDto>(
      `/api/tenants/${tenantId}/bug-reports/${bugReportId}`,
    );
    return response.data;
  }

  async getBugReportLogs(tenantId: string, bugReportId: string): Promise<BugReportLogEntryDto[]> {
    const response = await this.client.get<BugReportLogEntryDto[]>(
      `/api/tenants/${tenantId}/bug-reports/${bugReportId}/logs`,
    );
    return response.data;
  }

  async updateBugReportStatus(
    tenantId: string,
    bugReportId: string,
    status: string,
  ): Promise<void> {
    logger.logUserAction('Update bug report status', { tenantId, bugReportId, status });
    await this.patch(`/api/tenants/${tenantId}/bug-reports/${bugReportId}/status`, { status });
  }

  async mergeBugReports(
    tenantId: string,
    primaryBugReportId: string,
    secondaryBugReportIds: string[],
  ): Promise<BugReportDetailDto> {
    logger.logUserAction('Merge bug reports', { tenantId, primaryBugReportId, secondaryBugReportIds });
    const response = await this.client.post<BugReportDetailDto>(
      `/api/tenants/${tenantId}/bug-reports/merge`,
      { primaryBugReportId, secondaryBugReportIds },
    );
    return response.data;
  }

  async deleteBugReports(
    tenantId: string,
    bugReportIds: string[],
  ): Promise<{ deletedCount: number }> {
    logger.logUserAction('Delete bug reports', { tenantId, bugReportIds });
    const response = await this.client.delete<{ deletedCount: number }>(
      `/api/tenants/${tenantId}/bug-reports`,
      { data: { bugReportIds } },
    );
    return response.data;
  }

  async deduplicateBugReports(tenantId: string): Promise<DeduplicationResultDto> {
    logger.logUserAction('Deduplicate bug reports', { tenantId });
    const response = await this.client.post<DeduplicationResultDto>(
      `/api/tenants/${tenantId}/bug-reports/deduplicate`,
    );
    return response.data;
  }

  async analyzeBugReports(
    tenantId: string,
  ): Promise<{ successfullyAnalyzed: number; totalProcessed: number; failed: number; skipped: number }> {
    logger.logUserAction('Analyze bug reports', { tenantId });
    const response = await this.client.post<{
      successfullyAnalyzed: number;
      totalProcessed: number;
      failed: number;
      skipped: number;
    }>(`/api/tenants/${tenantId}/bug-reports/analyze`);
    return response.data;
  }

  async reportClientErrors(errors: ClientErrorReport[]): Promise<void> {
    try {
      const tenantId = this.getTenantIdFromToken();
      await this.client.post(`/api/tenants/${tenantId}/client-errors`, { errors });
    } catch {
      // Silently fail to prevent error loops (also swallows "no auth token" for unauthenticated pages).
    }
  }

  // On-Call Schedules
  async getOnCallSchedules(tenantId: string): Promise<OnCallScheduleDto[]> {
    const r = await this.client.get<OnCallScheduleDto[]>(`/api/tenants/${tenantId}/on-call`);
    return r.data;
  }
  async getOnCallSchedule(tenantId: string, id: string): Promise<OnCallScheduleDto> {
    const r = await this.client.get<OnCallScheduleDto>(`/api/tenants/${tenantId}/on-call/${id}`);
    return r.data;
  }
  async createOnCallSchedule(
    tenantId: string,
    data: CreateOnCallScheduleRequest,
  ): Promise<OnCallScheduleDto> {
    const r = await this.client.post<OnCallScheduleDto>(`/api/tenants/${tenantId}/on-call`, data);
    return r.data;
  }
  async updateOnCallSchedule(
    tenantId: string,
    id: string,
    data: UpdateOnCallScheduleRequest,
  ): Promise<OnCallScheduleDto> {
    const r = await this.client.put<OnCallScheduleDto>(`/api/tenants/${tenantId}/on-call/${id}`, data);
    return r.data;
  }
  async deleteOnCallSchedule(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/on-call/${id}`);
  }
  async addOnCallRotation(
    tenantId: string,
    scheduleId: string,
    data: AddOnCallRotationRequest,
  ): Promise<OnCallRotationDto> {
    const r = await this.client.post<OnCallRotationDto>(`/api/tenants/${tenantId}/on-call/${scheduleId}/rotations`, data);
    return r.data;
  }
  async addOnCallParticipant(
    tenantId: string,
    scheduleId: string,
    rotationId: string,
    data: AddOnCallParticipantRequest,
  ): Promise<OnCallParticipantDto> {
    const r = await this.client.post<OnCallParticipantDto>(`/api/tenants/${tenantId}/on-call/${scheduleId}/rotations/${rotationId}/participants`, data);
    return r.data;
  }
  async removeOnCallRotation(tenantId: string, scheduleId: string, rotationId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/on-call/${scheduleId}/rotations/${rotationId}`);
  }
  async removeOnCallParticipant(
    tenantId: string,
    scheduleId: string,
    rotationId: string,
    participantId: string,
  ): Promise<void> {
    await this.client.delete(
      `/api/tenants/${tenantId}/on-call/${scheduleId}/rotations/${rotationId}/participants/${participantId}`,
    );
  }
  async updateOnCallParticipant(
    tenantId: string,
    scheduleId: string,
    rotationId: string,
    participantId: string,
    data: UpdateOnCallParticipantRequest,
  ): Promise<OnCallParticipantDto> {
    const r = await this.client.put<OnCallParticipantDto>(
      `/api/tenants/${tenantId}/on-call/${scheduleId}/rotations/${rotationId}/participants/${participantId}`,
      data,
    );
    return r.data;
  }
  async updateOnCallRotation(
    tenantId: string,
    scheduleId: string,
    rotationId: string,
    data: UpdateOnCallRotationRequest,
  ): Promise<OnCallRotationDto> {
    const r = await this.client.put<OnCallRotationDto>(
      `/api/tenants/${tenantId}/on-call/${scheduleId}/rotations/${rotationId}`,
      data,
    );
    return r.data;
  }
  async reorderOnCallRotations(
    tenantId: string,
    scheduleId: string,
    orderedRotationIds: string[],
  ): Promise<OnCallScheduleDto> {
    const r = await this.client.post<OnCallScheduleDto>(
      `/api/tenants/${tenantId}/on-call/${scheduleId}/rotations/reorder`,
      { orderedRotationIds },
    );
    return r.data;
  }
  async createOnCallOverride(
    tenantId: string,
    scheduleId: string,
    data: CreateOnCallOverrideRequest,
  ): Promise<OnCallOverrideDto> {
    const r = await this.client.post<OnCallOverrideDto>(`/api/tenants/${tenantId}/on-call/${scheduleId}/overrides`, data);
    return r.data;
  }
  async deleteOnCallOverride(tenantId: string, scheduleId: string, overrideId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/on-call/${scheduleId}/overrides/${overrideId}`);
  }
  async getOnCallTimeline(
    tenantId: string,
    scheduleId: string,
    start: string,
    end: string,
  ): Promise<OnCallTimelineEntryDto[]> {
    const r = await this.client.get<OnCallTimelineEntryDto[]>(
      `/api/tenants/${tenantId}/on-call/${scheduleId}/timeline?startDate=${start}&endDate=${end}`,
    );
    return r.data;
  }
  async whoIsOnCall(tenantId: string, scheduleId: string): Promise<WhoIsOnCallEntryDto[]> {
    const r = await this.client.get<WhoIsOnCallEntryDto[]>(
      `/api/tenants/${tenantId}/on-call/${scheduleId}/who-is-on-call`,
    );
    return r.data;
  }
  // Escalation Policies
  async getEscalationPolicies(tenantId: string): Promise<EscalationPolicyDto[]> {
    const r = await this.client.get<EscalationPolicyDto[]>(`/api/tenants/${tenantId}/oncall/escalation-policies`);
    return r.data;
  }
  async getEscalationPolicy(tenantId: string, id: string): Promise<EscalationPolicyDto> {
    const r = await this.client.get<EscalationPolicyDto>(`/api/tenants/${tenantId}/oncall/escalation-policies/${id}`);
    return r.data;
  }
  async createEscalationPolicy(
    tenantId: string,
    data: CreateEscalationPolicyRequest,
  ): Promise<EscalationPolicyDto> {
    const r = await this.client.post<EscalationPolicyDto>(`/api/tenants/${tenantId}/oncall/escalation-policies`, data);
    return r.data;
  }
  async updateEscalationPolicy(
    tenantId: string,
    id: string,
    data: CreateEscalationPolicyRequest,
  ): Promise<EscalationPolicyDto> {
    const r = await this.client.put<EscalationPolicyDto>(`/api/tenants/${tenantId}/oncall/escalation-policies/${id}`, data);
    return r.data;
  }
  async deleteEscalationPolicy(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/oncall/escalation-policies/${id}`);
  }
  async reorderEscalationRules(tenantId: string, policyId: string, orderedRuleIds: string[]): Promise<EscalationPolicyDto> {
    const response = await this.client.put<EscalationPolicyDto>(
      `/api/tenants/${tenantId}/oncall/escalation-policies/${policyId}/reorder-rules`,
      { tenantId, policyId, orderedRuleIds }
    );
    return response.data;
  }

  // SLA Schedules (list only — for Runtime defaults dropdown)
  async getSlaSchedules(tenantId: string): Promise<AgreementsTypes.SlaScheduleSummaryDto[]> {
    const r = await this.client.get<AgreementsTypes.SlaScheduleSummaryDto[]>(
      `/api/tenants/${tenantId}/sla-schedules`,
    );
    return r.data;
  }
  async getSlaScheduleDetail(
    tenantId: string,
    id: string,
  ): Promise<AgreementsTypes.SlaScheduleDetailDto> {
    const r = await this.client.get<AgreementsTypes.SlaScheduleDetailDto>(
      `/api/tenants/${tenantId}/sla-schedules/${id}`,
    );
    return r.data;
  }
  async getSlaScheduleUsage(
    tenantId: string,
    id: string,
  ): Promise<AgreementsTypes.SlaScheduleUsageDto> {
    const r = await this.client.get<AgreementsTypes.SlaScheduleUsageDto>(
      `/api/tenants/${tenantId}/sla-schedules/${id}/usage`,
    );
    return r.data;
  }
  async createSlaSchedule(
    tenantId: string,
    data: AgreementsTypes.CreateSlaScheduleRequest,
  ): Promise<AgreementsTypes.SlaScheduleDetailDto> {
    const r = await this.client.post<AgreementsTypes.SlaScheduleDetailDto>(
      `/api/tenants/${tenantId}/sla-schedules`,
      data,
    );
    return r.data;
  }
  async updateSlaSchedule(
    tenantId: string,
    id: string,
    data: AgreementsTypes.UpdateSlaScheduleRequest,
  ): Promise<AgreementsTypes.SlaScheduleDetailDto> {
    const r = await this.client.put<AgreementsTypes.SlaScheduleDetailDto>(
      `/api/tenants/${tenantId}/sla-schedules/${id}`,
      data,
    );
    return r.data;
  }
  async deleteSlaSchedule(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/sla-schedules/${id}`);
  }

  // Holiday Calendars
  async getHolidayCalendars(tenantId: string): Promise<HolidayCalendarDto[]> {
    const r = await this.client.get<HolidayCalendarDto[]>(`/api/tenants/${tenantId}/holiday-calendars`);
    return r.data;
  }
  async getHolidayCalendar(tenantId: string, id: string): Promise<HolidayCalendarDto> {
    const r = await this.client.get<HolidayCalendarDto>(`/api/tenants/${tenantId}/holiday-calendars/${id}`);
    return r.data;
  }
  async createHolidayCalendar(
    tenantId: string,
    data: CreateHolidayCalendarRequest,
  ): Promise<HolidayCalendarDto> {
    const r = await this.client.post<HolidayCalendarDto>(`/api/tenants/${tenantId}/holiday-calendars`, data);
    return r.data;
  }
  async importCountryHolidays(
    tenantId: string,
    calendarId: string,
    data: { countryCode: string; year: number },
  ): Promise<unknown> {
    const r = await this.client.post<unknown>(`/api/tenants/${tenantId}/holiday-calendars/${calendarId}/import`, data);
    return r.data;
  }
  async addHolidayToCalendar(
    tenantId: string,
    calendarId: string,
    data: AddHolidayToCalendarRequest,
  ): Promise<HolidayDto> {
    const r = await this.client.post<HolidayDto>(`/api/tenants/${tenantId}/holiday-calendars/${calendarId}/holidays`, data);
    return r.data;
  }
  async getAvailableCountries(tenantId: string): Promise<{ code: string; name: string }[]> {
    const r = await this.client.get(`/api/tenants/${tenantId}/holiday-calendars/available-countries`);
    return r.data;
  }
  async updateHolidayInCalendar(
    tenantId: string,
    calendarId: string,
    holidayId: string,
    data: AddHolidayToCalendarRequest,
  ): Promise<HolidayDto> {
    const r = await this.client.put<HolidayDto>(
      `/api/tenants/${tenantId}/holiday-calendars/${calendarId}/holidays/${holidayId}`,
      data,
    );
    return r.data;
  }
  async deleteHolidayFromCalendar(
    tenantId: string,
    calendarId: string,
    holidayId: string,
  ): Promise<void> {
    await this.client.delete(
      `/api/tenants/${tenantId}/holiday-calendars/${calendarId}/holidays/${holidayId}`,
    );
  }

  async createSlaPolicy(
    tenantId: string,
    data: CreateSlaPolicyDto,
  ): Promise<SlaPolicy> {
    logger.logUserAction('Create SLA policy', { tenantId, name: data.name });
    const response = await this.client.post<SlaPolicy>(
      `/api/tenants/${tenantId}/sla-policies`,
      data,
    );
    logger.info('SLA policy created successfully', {
      policyId: response.data.id,
    });
    return response.data;
  }

  async updateSlaPolicy(
    tenantId: string,
    policyId: string,
    data: CreateSlaPolicyDto,
  ): Promise<SlaPolicy> {
    logger.logUserAction('Update SLA policy', { tenantId, policyId, name: data.name });
    const response = await this.client.put<SlaPolicy>(
      `/api/tenants/${tenantId}/sla-policies/${policyId}`,
      data,
    );
    logger.info('SLA policy updated successfully', {
      policyId: response.data.id,
    });
    return response.data;
  }

  async deleteSlaPolicy(
    tenantId: string,
    policyId: string,
  ): Promise<void> {
    logger.logUserAction('Delete SLA policy', { tenantId, policyId });
    await this.client.delete(
      `/api/tenants/${tenantId}/sla-policies/${policyId}`,
    );
    logger.info('SLA policy deleted successfully', { policyId });
  }

  async toggleSlaPolicyStatus(
    tenantId: string,
    policyId: string,
    isActive: boolean,
  ): Promise<SlaPolicy> {
    logger.logUserAction('Toggle SLA policy status', { tenantId, policyId, isActive });
    const response = await this.client.patch<SlaPolicy>(
      `/api/tenants/${tenantId}/sla-policies/${policyId}/toggle-status`,
      { isActive },
    );
    logger.info('SLA policy status toggled successfully', {
      policyId: response.data.id,
      isActive: response.data.isActive,
    });
    return response.data;
  }

  // Priority Configuration endpoints
  async getTenantPriorityConfig(
    tenantId: string,
    customerId?: string,
  ): Promise<TenantPriorityConfigDto> {
    logger.logUserAction('Get tenant priority configuration', { tenantId, customerId });
    const response = await this.client.get<TenantPriorityConfigDto>(
      `/api/tenants/${tenantId}/priority-configuration`,
      {
        params: {
          ...(customerId ? { customerId } : {}),
        },
      },
    );
    return normalizeTenantPriorityConfig(response.data);
  }

  async updateTenantPriorityConfig(
    tenantId: string,
    data: UpdateTenantPriorityConfigRequestDto,
  ): Promise<TenantPriorityConfigDto> {
    logger.logUserAction('Update tenant priority configuration', {
      tenantId,
      customerId: data.customerId,
      priorityLevelsCount: data.priorityLevels?.length ?? 0,
      impactLevelsCount: data.impactLevels?.length ?? 0,
      urgencyLevelsCount: data.urgencyLevels?.length ?? 0,
      priorityMatrixCount: data.priorityMatrix?.length ?? 0,
      isActive: data.isActive,
    });
    const payload: UpdateTenantPriorityConfigRequestDto = {
      customerId: data.customerId,
      priorityLevels: Array.isArray(data.priorityLevels) ? data.priorityLevels : [],
      impactLevels: Array.isArray(data.impactLevels) ? data.impactLevels : [],
      urgencyLevels: Array.isArray(data.urgencyLevels) ? data.urgencyLevels : [],
      priorityMatrix: Array.isArray(data.priorityMatrix) ? data.priorityMatrix : [],
      templateName: data.templateName,
      isActive: data.isActive,
    };
    const response = await this.client.put<TenantPriorityConfigDto>(
      `/api/tenants/${tenantId}/priority-configuration`,
      payload,
    );
    logger.info('Tenant priority configuration updated successfully', {
      tenantId,
      customerId: data.customerId,
      configId: response.data.id,
    });
    return normalizeTenantPriorityConfig(response.data);
  }

  async getSystemPriorityTemplates(tenantId?: string): Promise<SystemPriorityTemplateDto[]> {
    logger.logUserAction('Get system priority templates', { tenantId });
    const endpoint = tenantId
      ? `/api/tenants/${tenantId}/priority-configuration/templates`
      : '/api/system/priority-templates';
    const response = await this.client.get<SystemPriorityTemplateDto[]>(endpoint);
    return response.data.map((template) => normalizeSystemPriorityTemplate(template));
  }

  async createSystemPriorityTemplate(
    data: UpsertSystemPriorityTemplateRequest,
  ): Promise<SystemPriorityTemplateDto> {
    logger.logUserAction('Create system priority template', { name: data.name, isDefault: data.isDefault });
    const response = await this.client.post<SystemPriorityTemplateDto>(
      '/api/system/priority-templates',
      data,
    );
    return normalizeSystemPriorityTemplate(response.data);
  }

  async updateSystemPriorityTemplate(
    id: string,
    data: UpsertSystemPriorityTemplateRequest,
  ): Promise<void> {
    logger.logUserAction('Update system priority template', { id, name: data.name, isDefault: data.isDefault });
    await this.client.put(`/api/system/priority-templates/${id}`, data);
  }

  async deleteSystemPriorityTemplate(id: string): Promise<void> {
    logger.logUserAction('Delete system priority template', { id });
    await this.client.delete(`/api/system/priority-templates/${id}`);
  }

  async applyPriorityTemplate(
    tenantId: string,
    data: ApplyPriorityTemplateRequestDto,
  ): Promise<TenantPriorityConfigDto> {
    logger.logUserAction('Apply priority template', {
      tenantId,
      templateId: data.templateId,
      customerId: data.customerId,
    });
    const response = await this.client.post<TenantPriorityConfigDto>(
      `/api/tenants/${tenantId}/priority-configuration/apply-template`,
      data,
    );
    logger.info('Priority template applied successfully', {
      tenantId,
      templateId: data.templateId,
      configId: response.data.id,
    });
    return normalizeTenantPriorityConfig(response.data);
  }

  // Reason Configuration endpoints
  async getTenantReasonConfig(tenantId: string, customerId?: string): Promise<TenantReasonConfigDto> {
    logger.logUserAction('Get reason config', { tenantId });
    const params = customerId ? `?customerId=${customerId}` : '';
    const response = await this.client.get<TenantReasonConfigDto>(
      `/api/tenants/${tenantId}/reason-config${params}`,
    );
    return response.data;
  }

  async updateTenantReasonConfig(
    tenantId: string,
    data: UpdateTenantReasonConfigRequestDto,
  ): Promise<TenantReasonConfigDto> {
    logger.logUserAction('Update reason config', { tenantId });
    const response = await this.client.put<TenantReasonConfigDto>(
      `/api/tenants/${tenantId}/reason-config`,
      data,
    );
    return response.data;
  }

  async getSystemReasonTemplates(): Promise<SystemReasonTemplateDto[]> {
    logger.logUserAction('Get system reason templates');
    const response = await this.client.get<SystemReasonTemplateDto[]>(
      '/api/system/reason-templates',
    );
    return response.data;
  }

  async createSystemReasonTemplate(
    data: UpsertSystemReasonTemplateRequest,
  ): Promise<SystemReasonTemplateDto> {
    logger.logUserAction('Create system reason template', { name: data.name, isDefault: data.isDefault });
    const response = await this.client.post<SystemReasonTemplateDto>(
      '/api/system/reason-templates',
      data,
    );
    return response.data;
  }

  async updateSystemReasonTemplate(
    id: string,
    data: UpsertSystemReasonTemplateRequest,
  ): Promise<void> {
    logger.logUserAction('Update system reason template', { id, name: data.name, isDefault: data.isDefault });
    await this.client.put(`/api/system/reason-templates/${id}`, data);
  }

  async deleteSystemReasonTemplate(id: string): Promise<void> {
    logger.logUserAction('Delete system reason template', { id });
    await this.client.delete(`/api/system/reason-templates/${id}`);
  }

  async getSystemSlaPolicyTemplates(): Promise<SystemSlaPolicyTemplateDto[]> {
    logger.logUserAction('Get system SLA policy templates');
    const response = await this.client.get<SystemSlaPolicyTemplateDto[]>(
      '/api/system/sla-templates',
    );
    return response.data.map((template) => normalizeSystemSlaPolicyTemplate(template));
  }

  async createSystemSlaPolicyTemplate(
    data: CreateSystemSlaPolicyTemplateDto,
  ): Promise<SystemSlaPolicyTemplateDto> {
    logger.logUserAction('Create system SLA policy template', {
      name: data.name,
      isDefault: data.isDefault,
    });
    const response = await this.client.post<SystemSlaPolicyTemplateDto>(
      '/api/system/sla-templates',
      data,
    );
    logger.info('System SLA policy template created successfully', {
      templateId: response.data.id,
    });
    return normalizeSystemSlaPolicyTemplate(response.data);
  }

  async updateSystemSlaPolicyTemplate(
    id: string,
    data: UpdateSystemSlaPolicyTemplateDto,
  ): Promise<void> {
    logger.logUserAction('Update system SLA policy template', {
      id,
      name: data.name,
      isDefault: data.isDefault,
    });
    await this.client.put(
      `/api/system/sla-templates/${id}`,
      data,
    );
    logger.info('System SLA policy template updated successfully', { id });
  }

  async deleteSystemSlaPolicyTemplate(id: string): Promise<void> {
    logger.logUserAction('Delete system SLA policy template', { id });
    await this.client.delete(
      `/api/system/sla-templates/${id}`,
    );
    logger.info('System SLA policy template deleted successfully', { id });
  }

  async applyReasonTemplate(
    tenantId: string,
    data: ApplyReasonTemplateRequestDto,
  ): Promise<TenantReasonConfigDto> {
    logger.logUserAction('Apply reason template', { tenantId, templateId: data.templateId });
    const response = await this.client.post<TenantReasonConfigDto>(
      `/api/tenants/${tenantId}/reason-config/apply-template`,
      data,
    );
    return response.data;
  }

  // Pipeline Configuration endpoints
  async getPipelineConfigurationByEndpoint(
    tenantId: string,
    endpointId: string,
  ): Promise<PipelineConfigurationDto | null> {
    try {
      const response = await this.client.get<PipelineConfigurationDto>(
        `/api/tenants/${tenantId}/pipeline-configurations/endpoint/${endpointId}`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateValidationRules(
    tenantId: string,
    pipelineId: string,
    data: UpdateValidationRulesRequest,
  ): Promise<PipelineConfigurationDto> {
    logger.logUserAction('Update validation rules', {
      tenantId,
      pipelineId,
      ruleCount: data.validationRules.length,
    });
    const response = await this.client.put<PipelineConfigurationDto>(
      `/api/tenants/${tenantId}/pipeline-configurations/${pipelineId}/validation-rules`,
      data,
    );
    logger.info('Validation rules updated successfully', {
      pipelineId,
      ruleCount: data.validationRules.length,
    });
    return response.data;
  }

  async resetPipelineConfigurationByEndpoint(
    tenantId: string,
    endpointId: string,
  ): Promise<PipelineConfigurationDto> {
    logger.logUserAction('Reset pipeline configuration', { tenantId, endpointId });
    const response = await this.client.post<PipelineConfigurationDto>(
      `/api/tenants/${tenantId}/pipeline-configurations/endpoint/${endpointId}/reset`,
    );
    logger.info('Pipeline configuration reset successfully', { endpointId });
    return response.data;
  }

  async getPipelineGraph(
    tenantId: string,
    pipelineId: string,
  ): Promise<PipelineGraphDto | null> {
    try {
      const response = await this.client.get<PipelineGraphDto>(
        `/api/tenants/${tenantId}/pipeline-configurations/${pipelineId}/graph`,
      );
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  }

  async getPipelineGraphByEndpoint(
    tenantId: string,
    endpointId: string,
  ): Promise<PipelineGraphDto | null> {
    try {
      const response = await this.client.get<PipelineGraphDto>(
        `/api/tenants/${tenantId}/pipeline-configurations/endpoint/${endpointId}/graph`,
      );
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  }

  async savePipelineGraph(
    tenantId: string,
    pipelineId: string,
    graph: { nodes: PipelineNodeDto[]; edges: PipelineEdgeDto[] },
  ): Promise<PipelineGraphDto> {
    logger.logUserAction('Save pipeline graph', { tenantId, pipelineId });
    const response = await this.client.put<PipelineGraphDto>(
      `/api/tenants/${tenantId}/pipeline-configurations/${pipelineId}/graph`,
      {
        nodes: graph.nodes.map((n) => ({
          id: n.id,
          nodeType: n.nodeType,
          stepType: n.stepType,
          name: n.name,
          positionX: n.positionX,
          positionY: n.positionY,
          configuration: n.configuration,
          isEnabled: n.isEnabled,
          conditionField: n.conditionField,
          conditionOperator: n.conditionOperator,
        })),
        edges: graph.edges.map((e) => ({
          id: e.id,
          sourceNodeId: e.sourceNodeId,
          targetNodeId: e.targetNodeId,
          label: e.label,
          conditionValue: e.conditionValue,
          sortOrder: e.sortOrder,
          isDefault: e.isDefault,
        })),
      },
    );
    logger.info('Pipeline graph saved', { pipelineId });
    return response.data;
  }

  async getValidationTemplate(
    tenantId: string,
    endpointType: number,
  ): Promise<ValidationTemplateDto | null> {
    logger.logUserAction('Get validation template', { tenantId, endpointType });
    try {
      const response = await this.client.get<ValidationTemplateDto>(
        `/api/tenants/${tenantId}/validation-templates?endpointType=${endpointType}`,
      );
      logger.info('Validation template retrieved', { endpointType });
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          logger.warn('No validation template found for endpoint type', { endpointType });
          return null;
        }
      }
      throw error;
    }
  }

  async getSystemDefaultValidationTemplates(): Promise<ValidationTemplateDto[]> {
    logger.logUserAction('Get system default validation templates');
    const response = await this.client.get<ValidationTemplateDto[]>(
      '/api/validation-templates/system-defaults',
    );
    logger.info('System default validation templates retrieved', { count: response.data.length });
    return response.data;
  }

  // Settings endpoints
  async getSystemSettings(): Promise<SystemSettings> {
    logger.logUserAction('Get system settings');
    const response = await this.client.get<SystemSettings>(
      '/api/system-settings',
    );
    logger.info('System settings retrieved successfully');
    return response.data;
  }

  async updateSystemSettings(
    settings: SystemSettings,
  ): Promise<SystemSettings> {
    logger.logUserAction('Update system settings', {
      sendGridEnabled: settings.sendGrid.enabled,
    });
    const response = await this.client.put<SystemSettings>(
      '/api/system-settings',
      settings,
    );
    logger.info('System settings updated successfully');
    return response.data;
  }

  async getAutoCloseStatus(): Promise<AutoCloseStatus> {
    logger.logUserAction('Get auto-close status');
    const response = await this.client.get<AutoCloseStatus>(
      '/api/system-monitoring/auto-close-status',
    );
    logger.info('Auto-close status retrieved successfully');
    return response.data;
  }

  async getScheduledJobs(): Promise<ScheduledJobDto[]> {
    logger.logUserAction('Get scheduled jobs');
    const response = await this.client.get<ScheduledJobDto[]>(
      '/api/system-monitoring/scheduled-jobs',
    );
    logger.info('Scheduled jobs retrieved successfully', { count: response.data.length });
    return response.data;
  }

  async getEmailTemplates(): Promise<EmailTemplateDto[]> {
    logger.logUserAction('Get email templates');
    const response = await this.client.get<EmailTemplateDto[]>(
      '/api/system/email-templates',
    );
    logger.info('Email templates retrieved successfully', { count: response.data.length });
    return response.data;
  }

  async getEmailTemplate(id: string): Promise<EmailTemplateDto> {
    logger.logUserAction('Get email template', { id });
    const response = await this.client.get<EmailTemplateDto>(
      `/api/system/email-templates/${id}`,
    );
    logger.info('Email template retrieved successfully', { id });
    return response.data;
  }

  async updateEmailTemplate(
    id: string,
    data: {
      subjectTemplate: string;
      bodyTemplate: string;
      isActive: boolean;
    },
  ): Promise<EmailTemplateDto> {
    logger.logUserAction('Update email template', { id, isActive: data.isActive });
    const response = await this.client.put<EmailTemplateDto>(
      `/api/system/email-templates/${id}`,
      data,
    );
    logger.info('Email template updated successfully', { id });
    return response.data;
  }

  async resetEmailTemplate(id: string): Promise<EmailTemplateDto> {
    logger.logUserAction('Reset email template', { id });
    const response = await this.client.post<EmailTemplateDto>(
      `/api/system/email-templates/${id}/reset`,
    );
    logger.info('Email template reset successfully', { id });
    return response.data;
  }

  async previewEmailTemplate(data: {
    subjectTemplate: string;
    bodyTemplate: string;
    notificationType: number;
  }): Promise<PreviewEmailTemplateResult> {
    logger.logUserAction('Preview email template', { notificationType: data.notificationType });
    const response = await this.client.post<PreviewEmailTemplateResult>(
      '/api/system/email-templates/preview',
      data,
    );
    logger.info('Email template preview generated successfully');
    return response.data;
  }

  // Pipeline defaults
  async getAllPipelineDefaults(): Promise<PipelineDefaultsListDto> {
    const response = await this.client.get<PipelineDefaultsListDto>(
      '/api/system-settings/pipeline-defaults',
    );
    return response.data;
  }

  async getPipelineDefaults(
    endpointType: string,
  ): Promise<PipelineDefaultsDto> {
    const response = await this.client.get<PipelineDefaultsDto>(
      `/api/system-settings/pipeline-defaults/${endpointType}`,
    );
    return response.data;
  }

  async updatePipelineDefaults(
    endpointType: string,
    data: Partial<PipelineDefaultsDto>,
  ): Promise<PipelineDefaultsDto> {
    const response = await this.client.put<PipelineDefaultsDto>(
      `/api/system-settings/pipeline-defaults/${endpointType}`,
      data,
    );
    return response.data;
  }

  async deletePipelineDefaults(endpointType: string): Promise<void> {
    await this.client.delete(
      `/api/system-settings/pipeline-defaults/${endpointType}`,
    );
  }

  async sendTestEmail(
    request: SendTestEmailRequest,
  ): Promise<SendTestEmailResult> {
    logger.logUserAction('Send test email', { toEmail: request.toEmail });
    const response = await this.client.post<SendTestEmailResult>(
      '/api/system-settings/test-email',
      request,
    );
    logger.info('Test email sent successfully');
    return response.data;
  }

  async getTenantSettings(): Promise<TenantSettings> {
    logger.logUserAction('Get tenant settings');
    const response = await this.client.get<TenantSettings>(
      '/api/tenant-settings',
    );
    logger.info('Tenant settings retrieved successfully');
    return response.data;
  }

  async updateTenantSettings(
    settings: Partial<TenantSettings>,
  ): Promise<TenantSettings> {
    logger.logUserAction('Update tenant settings', {
      companyName: settings.companyName,
    });
    const response = await this.client.put<TenantSettings>(
      '/api/tenant-settings',
      settings,
    );
    logger.info('Tenant settings updated successfully');
    return response.data;
  }

  async getTenantEmailSettings(tenantId: string): Promise<TenantEmailSettings> {
    logger.logUserAction('Get tenant email settings', { tenantId });
    const response = await this.client.get<TenantEmailSettings>(
      `/api/tenants/${tenantId}/settings/email`,
    );
    logger.info('Tenant email settings retrieved successfully', { tenantId });
    return response.data;
  }

  async updateTenantEmailSettings(
    tenantId: string,
    settings: Partial<TenantEmailSettings>,
  ): Promise<TenantEmailSettings> {
    logger.logUserAction('Update tenant email settings', {
      tenantId,
      useCustomSendGrid: settings.useCustomSendGrid,
    });
    const response = await this.client.put<TenantEmailSettings>(
      `/api/tenants/${tenantId}/settings/email`,
      settings,
    );
    logger.info('Tenant email settings updated successfully', { tenantId });
    return response.data;
  }

  async sendTenantTestEmail(
    tenantId: string,
    request: SendTenantTestEmailRequest,
  ): Promise<SendTenantTestEmailResult> {
    logger.logUserAction('Send tenant test email', { tenantId, toEmail: request.toEmail });
    const response = await this.client.post<SendTenantTestEmailResult>(
      `/api/tenants/${tenantId}/settings/email/test`,
      request,
    );
    logger.info('Tenant test email request completed', { tenantId, success: response.data.success });
    return response.data;
  }

  async getSlaPlatformDefaults(tenantId: string): Promise<SlaPlatformDefaults> {
    const response = await this.client.get<SlaPlatformDefaults>(
      `/api/tenants/${tenantId}/settings/sla-platform-defaults`,
    );
    return response.data;
  }

  async updateSlaPlatformDefaults(
    tenantId: string,
    settings: Partial<SlaPlatformDefaults>,
  ): Promise<SlaPlatformDefaults> {
    const response = await this.client.put<SlaPlatformDefaults>(
      `/api/tenants/${tenantId}/settings/sla-platform-defaults`,
      settings,
    );
    return response.data;
  }

  async getIntegrations(): Promise<Integration[]> {
    logger.logUserAction('Get integrations');
    const response = await this.client.get<Integration[]>('/api/integrations');
    logger.info('Integrations retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  async downloadTeamsManifest(): Promise<Blob> {
    logger.logUserAction('Download Teams app manifest');
    try {
      const response = await this.client.get<Blob>('/api/teams/manifest', {
        responseType: 'blob',
      });
      logger.info('Teams app manifest downloaded successfully');
      return response.data;
    } catch (error) {
      // axios returns the response body as a Blob even on error when responseType=blob.
      // Surface the RFC 7807 problem-detail title/detail so the user sees actionable
      // text instead of a generic "Request failed with status code 409".
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        let message = text || `HTTP ${error.response.status}`;
        try {
          const problem = JSON.parse(text) as { title?: string; detail?: string };
          if (problem.detail || problem.title) {
            message = problem.detail || problem.title || message;
          }
        } catch {
          // Not JSON — fall through and use the raw text.
        }
        throw new Error(message);
      }
      throw error;
    }
  }

  async createOrUpdateIntegration(integration: {
    id?: string;
    name: string;
    type: string;
    isEnabled: boolean;
    configurationData: string;
    isBidirectional?: boolean;
    description?: string;
    scope?: number;
    scopeEntityId?: string;
    channelIdentifier?: string;
  }): Promise<Integration> {
    logger.logUserAction('Create/update integration', {
      type: integration.type,
      enabled: integration.isEnabled,
      scope: integration.scope,
    });
    const response = await this.client.put<Integration>(
      '/api/integrations',
      integration,
    );
    logger.info('Integration saved successfully', { type: integration.type });
    return response.data;
  }

  // Configuration endpoint
  async getConfig(): Promise<{ ingestionApiUrl: string }> {
    const response = await this.client.get<{ ingestionApiUrl: string }>(
      '/api/config',
    );
    return response.data;
  }

  // Role Management endpoints
  async getRoles(
    tenantId: string,
    includePermissions = false,
    roleType?: number,
  ): Promise<RoleDto[]> {
    logger.logUserAction('Get roles', { tenantId, includePermissions, roleType });
    const params: Record<string, unknown> = { includePermissions };
    if (roleType !== undefined) params.roleType = roleType;
    const response = await this.client.get<RoleDto[]>(
      `/api/tenants/${tenantId}/roles`,
      { params },
    );
    logger.info('Roles retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  async getRole(tenantId: string, roleId: string): Promise<RoleDto> {
    logger.logUserAction('Get role', { tenantId, roleId });
    const response = await this.client.get<RoleDto>(
      `/api/tenants/${tenantId}/roles/${roleId}`,
    );
    logger.info('Role retrieved successfully', {
      roleName: response.data.name,
    });
    return response.data;
  }

  async createRole(
    tenantId: string,
    request: CreateRoleRequest,
  ): Promise<RoleDto> {
    logger.logUserAction('Create role', { tenantId, roleName: request.name });
    const response = await this.client.post<RoleDto>(
      `/api/tenants/${tenantId}/roles`,
      request,
    );
    logger.info('Role created successfully', { roleId: response.data.id });
    return response.data;
  }

  async updateRole(
    tenantId: string,
    roleId: string,
    request: UpdateRoleRequest,
  ): Promise<RoleDto> {
    logger.logUserAction('Update role', {
      tenantId,
      roleId,
      roleName: request.name,
    });
    const response = await this.client.put<RoleDto>(
      `/api/tenants/${tenantId}/roles/${roleId}`,
      request,
    );
    logger.info('Role updated successfully');
    return response.data;
  }

  async deleteRole(tenantId: string, roleId: string): Promise<void> {
    logger.logUserAction('Delete role', { tenantId, roleId });
    await this.client.delete(`/api/tenants/${tenantId}/roles/${roleId}`);
    logger.info('Role deleted successfully');
  }

  async duplicateRole(
    tenantId: string,
    roleId: string,
    request: DuplicateRoleRequest,
  ): Promise<RoleDto> {
    logger.logUserAction('Duplicate role', {
      tenantId,
      roleId,
      newRoleName: request.name,
    });
    const response = await this.client.post<RoleDto>(
      `/api/tenants/${tenantId}/roles/${roleId}/duplicate`,
      request,
    );
    logger.info('Role duplicated successfully', { roleId: response.data.id });
    return response.data;
  }

  async assignPermissionsToRole(
    tenantId: string,
    roleId: string,
    request: AssignPermissionsRequest,
  ): Promise<RoleDto> {
    logger.logUserAction('Assign permissions to role', {
      tenantId,
      roleId,
      permissionCount: request.permissionIds.length,
    });
    const response = await this.client.put<RoleDto>(
      `/api/tenants/${tenantId}/roles/${roleId}/permissions`,
      request,
    );
    logger.info('Permissions assigned successfully');
    return response.data;
  }

  async getRolePermissions(
    tenantId: string,
    roleId: string,
  ): Promise<PermissionDto[]> {
    logger.logUserAction('Get role permissions', { tenantId, roleId });
    const response = await this.client.get<PermissionDto[]>(
      `/api/tenants/${tenantId}/roles/${roleId}/permissions`,
    );
    logger.info('Role permissions retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  // Permission Management endpoints
  async getAllPermissions(
    category?: string,
    resourceType?: string,
  ): Promise<PermissionDto[]> {
    logger.logUserAction('Get all permissions', { category, resourceType });
    const response = await this.client.get<PermissionDto[]>(
      '/api/permissions',
      {
        params: { category, resourceType },
      },
    );
    logger.info('Permissions retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  async getPermissionCategories(): Promise<{ categories: string[] }> {
    logger.logUserAction('Get permission categories');
    const response = await this.client.get<{ categories: string[] }>(
      '/api/permissions/categories',
    );
    logger.info('Permission categories retrieved successfully', {
      count: response.data.categories.length,
    });
    return response.data;
  }

  async getPermissionsGrouped(): Promise<PermissionCategoryGroup[]> {
    logger.logUserAction('Get permissions grouped');
    const response = await this.client.get<PermissionCategoryGroup[]>(
      '/api/permissions/grouped',
    );
    logger.info('Grouped permissions retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  // Teams Management endpoints
  async getTeams(
    tenantId: string,
    params?: { customerId?: string; includeInactive?: boolean },
    signal?: AbortSignal,
  ): Promise<Team[]> {
    logger.logUserAction('Get teams', { tenantId, ...params });
    const response = await this.client.get<Team[]>(
      `/api/tenants/${tenantId}/teams`,
      { params, signal },
    );
    logger.info('Teams retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  async getTeamWorkload(tenantId: string): Promise<WorkloadSummaryDto> {
    logger.logUserAction('Get team workload', { tenantId });
    const response = await this.client.get<WorkloadSummaryDto>(
      `/api/tenants/${tenantId}/teams/workload`,
    );
    logger.info('Team workload retrieved successfully');
    return response.data;
  }

  async getTeam(tenantId: string, teamId: string, signal?: AbortSignal): Promise<TeamDetailDto> {
    logger.logUserAction('Get team', { tenantId, teamId });
    const response = await this.client.get<TeamDetailDto>(
      `/api/tenants/${tenantId}/teams/${teamId}`,
      { signal },
    );
    logger.info('Team retrieved successfully');
    return response.data;
  }

  // Alias for getTeam
  async getTeamById(tenantId: string, teamId: string, signal?: AbortSignal): Promise<TeamDetailDto> {
    return this.getTeam(tenantId, teamId, signal);
  }

  async createTeam(tenantId: string, data: CreateTeamDto): Promise<Team> {
    logger.logUserAction('Create team', { tenantId, name: data.name });
    const response = await this.client.post<Team>(
      `/api/tenants/${tenantId}/teams`,
      data,
    );
    logger.info('Team created successfully', { teamId: response.data.id });
    return response.data;
  }

  async updateTeam(
    tenantId: string,
    teamId: string,
    data: UpdateTeamDto,
  ): Promise<Team> {
    logger.logUserAction('Update team', { tenantId, teamId });
    const response = await this.client.put<Team>(
      `/api/tenants/${tenantId}/teams/${teamId}`,
      data,
    );
    logger.info('Team updated successfully');
    return response.data;
  }

  async deleteTeam(tenantId: string, teamId: string): Promise<void> {
    logger.logUserAction('Delete team', { tenantId, teamId });
    await this.client.delete(`/api/tenants/${tenantId}/teams/${teamId}`);
    logger.info('Team deleted successfully');
  }

  async getDeletedTeams(tenantId: string): Promise<Team[]> {
    logger.logUserAction('Get deleted teams', { tenantId });
    const response = await this.client.get<Team[]>(
      `/api/tenants/${tenantId}/teams/deleted`,
    );
    logger.info('Deleted teams retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  async restoreTeam(tenantId: string, teamId: string): Promise<Team> {
    logger.logUserAction('Restore team', { tenantId, teamId });
    const response = await this.client.post<Team>(
      `/api/tenants/${tenantId}/teams/${teamId}/restore`,
    );
    logger.info('Team restored successfully', { teamId: response.data.id });
    return response.data;
  }

  async purgeTeam(tenantId: string, teamId: string): Promise<void> {
    logger.logUserAction('Purge team', { tenantId, teamId });
    await this.client.delete(`/api/tenants/${tenantId}/teams/${teamId}/purge`);
    logger.info('Team purged successfully', { teamId });
  }

  async getTeamMembers(
    tenantId: string,
    teamId: string,
  ): Promise<TeamMember[]> {
    logger.logUserAction('Get team members', { tenantId, teamId });
    const response = await this.client.get<TeamMember[]>(
      `/api/tenants/${tenantId}/teams/${teamId}/members`,
    );
    logger.info('Team members retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  async addTeamMember(
    tenantId: string,
    teamId: string,
    data: AddTeamMemberDto,
  ): Promise<void> {
    logger.logUserAction('Add team member', {
      tenantId,
      teamId,
      userId: data.userId,
    });
    await this.client.post(
      `/api/tenants/${tenantId}/teams/${teamId}/members/${data.userId}`,
    );
    logger.info('Team member added successfully');
  }

  async removeTeamMember(
    tenantId: string,
    teamId: string,
    userId: string,
  ): Promise<void> {
    logger.logUserAction('Remove team member', { tenantId, teamId, userId });
    await this.client.delete(
      `/api/tenants/${tenantId}/teams/${teamId}/members/${userId}`,
    );
    logger.info('Team member removed successfully');
  }

  async getTeamsAnalytics(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<TeamsAnalyticsDto> {
    logger.logUserAction('Get Teams analytics', params);
    const response = await this.client.get<TeamsAnalyticsDto>(
      '/api/teams/analytics',
      { params },
    );
    logger.info('Teams analytics retrieved successfully');
    return response.data;
  }

  async getTeamsEngagement(params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }): Promise<TeamsEngagementDto> {
    logger.logUserAction('Get Teams engagement analytics', params);
    const response = await this.client.get<TeamsEngagementDto>(
      '/api/teams/analytics/engagement',
      { params },
    );
    logger.info('Teams engagement analytics retrieved successfully');
    return response.data;
  }

  private handleApiError(error: unknown, fallbackMessage: string): never {
    if (axios.isAxiosError<ApiError>(error)) {
      const message = error.response?.data?.message ?? error.message ?? fallbackMessage;
      throw new Error(message);
    }

    if (error instanceof Error) {
      throw error;
    }

    throw new Error(fallbackMessage);
  }

  async getPerformanceSummary(hours = 24): Promise<PerformanceSummaryDto> {
    logger.logUserAction('Get performance summary', { hours });
    try {
      const response = await this.client.get<PerformanceSummaryDto>(
        '/api/system/performance/summary',
        { params: { hours } },
      );
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to load performance summary');
    }
  }

  async getSlowQueries(
    hours = 24,
    limit = 50,
    requestPath?: string,
    table?: string,
  ): Promise<SlowQueryDto[]> {
    logger.logUserAction('Get slow queries', { hours, limit, requestPath, table });
    try {
      const response = await this.client.get<SlowQueryDto[]>(
        '/api/system/performance/slow-queries',
        {
          params: {
            hours,
            limit,
            requestPath,
            table,
          },
        },
      );
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to load slow queries');
    }
  }

  async getGroupedSlowQueries(
    hours: number,
    limit?: number,
  ): Promise<GroupedSlowQueryDto[]> {
    logger.logUserAction('Get grouped slow queries', { hours, limit });
    try {
      const response = await this.client.get<GroupedSlowQueryDto[]>(
        '/api/system/performance/slow-queries/grouped',
        {
          params: {
            hours,
            limit,
          },
        },
      );
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to load grouped slow queries');
    }
  }

  async getPerformanceByEndpoint(hours = 24): Promise<EndpointPerformanceDto[]> {
    logger.logUserAction('Get performance by endpoint', { hours });
    try {
      const response = await this.client.get<EndpointPerformanceDto[]>(
        '/api/system/performance/by-endpoint',
        { params: { hours } },
      );
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to load endpoint performance');
    }
  }

  async getPerformanceByTable(hours = 24): Promise<TablePerformanceDto[]> {
    logger.logUserAction('Get performance by table', { hours });
    try {
      const response = await this.client.get<TablePerformanceDto[]>(
        '/api/system/performance/by-table',
        { params: { hours } },
      );
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to load table performance');
    }
  }

  async getPerformanceTrends(
    hours = 24,
    interval = 'hourly',
  ): Promise<PerformanceTrendBucketDto[]> {
    logger.logUserAction('Get performance trends', { hours, interval });
    try {
      const response = await this.client.get<PerformanceTrendBucketDto[]>(
        '/api/system/performance/trends',
        { params: { hours, interval } },
      );
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to load performance trends');
    }
  }

  // System Logs
  async getSystemLogs(params: {
    level?: LogLevel;
    excludeLevel?: LogLevel;
    fromDate?: string;
    toDate?: string;
    searchText?: string;
    page?: number;
    pageSize?: number;
  }): Promise<SystemLogsResponse> {
    logger.logUserAction('Get system logs', params);
    const queryParams = new URLSearchParams();
    if (params.level !== undefined) queryParams.append('level', params.level.toString());
    if (params.excludeLevel !== undefined) queryParams.append('excludeLevel', params.excludeLevel.toString());
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.searchText) queryParams.append('searchText', params.searchText);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const response = await this.client.get<SystemLogsResponse>(
      `/api/system/logs?${queryParams.toString()}`,
    );
    logger.info('System logs retrieved successfully', {
      totalCount: response.data.totalCount,
    });
    return response.data;
  }

  async getEventStreamLogs(
    tenantId: string,
    eventId: string,
    params?: {
      after?: string;
      level?: number;
    },
  ): Promise<SystemLogDto[]> {
    const queryParams = new URLSearchParams();
    if (params?.after) queryParams.append('after', params.after);
    if (params?.level !== undefined) queryParams.append('level', params.level.toString());

    const queryString = queryParams.toString();
    const url = `/api/tenants/${tenantId}/events/${eventId}/logs${queryString ? `?${queryString}` : ''}`;
    const response = await this.client.get<SystemLogDto[]>(url);
    return response.data;
  }

  async getSystemLogHealth(): Promise<import('../types').SystemLogHealth> {
    logger.logUserAction('Get system log health');
    const response = await this.client.get<import('../types').SystemLogHealth>('/api/system/logs/health');
    logger.info('System log health retrieved successfully');
    return response.data;
  }

  async getMigrationHealth(): Promise<MigrationHealthStatusResponse> {
    logger.logUserAction('Get database migration health');
    const response = await this.client.get<MigrationHealthStatusResponse>('/health/migrations');
    logger.info('Database migration health retrieved successfully');
    return response.data;
  }

  async truncateSystemLogs(): Promise<{ deletedCount: number }> {
    const response = await this.client.delete<{ deletedCount: number }>('/api/system/logs');
    return response.data;
  }

  async truncateSystemLogsStreaming(
    onProgress: (progress: BatchDeleteStreamUpdate) => void,
  ): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${this.baseUrl}/api/system/logs/truncate`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Truncation failed: ${response.status} ${response.statusText}`);
    }

    await this.readNdjsonStream<BatchDeleteStreamUpdate>(response, onProgress);
  }

  private async readNdjsonStream<T>(
    response: Response,
    onData: (data: T) => void,
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed) {
            try {
              onData(JSON.parse(trimmed) as T);
            } catch {
              // Skip malformed lines
            }
          }
        }
      }

      const remaining = buffer.trim();
      if (remaining) {
        try {
          onData(JSON.parse(remaining) as T);
        } catch {
          // Skip malformed remaining data
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // Sign-In Logs endpoints
  async getSignInLogs(params: {
    email?: string;
    fromDate?: string;
    toDate?: string;
    success?: boolean;
    authMethod?: string;
    page?: number;
    pageSize?: number;
  }): Promise<import('../types').SignInLogsResponse> {
    logger.logUserAction('Get sign-in logs', params);
    const queryParams = new URLSearchParams();
    if (params.email) queryParams.append('email', params.email);
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.success !== undefined) queryParams.append('success', params.success.toString());
    if (params.authMethod) queryParams.append('authMethod', params.authMethod);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const response = await this.client.get<import('../types').SignInLogsResponse>(
      `/api/signin-logs?${queryParams.toString()}`,
    );
    logger.info('Sign-in logs retrieved successfully', {
      totalCount: response.data.totalCount,
    });
    return response.data;
  }

  async getEmailLogs(
    tenantId: string,
    params: GetEmailLogsParams,
  ): Promise<EmailLogDto[]> {
    logger.logUserAction('Get email logs', { tenantId, ...params });
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.status) queryParams.append('status', params.status);
    if (params.triggerType) queryParams.append('triggerType', params.triggerType);
    if (params.search) queryParams.append('search', params.search);
    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.take !== undefined) queryParams.append('take', params.take.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/tenants/${tenantId}/email-logs${queryString ? `?${queryString}` : ''}`;
    const response = await this.client.get<EmailLogDto[]>(endpoint);
    logger.info('Email logs retrieved successfully', {
      tenantId,
      count: response.data.length,
    });
    return response.data;
  }

  // Avatar upload endpoint
  async uploadAvatar(file: File): Promise<import('../types').UploadAvatarResponse> {
    logger.logUserAction('Upload avatar', { fileName: file.name, fileSize: file.size });
    
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<import('../types').UploadAvatarResponse>(
      '/api/users/profile/avatar',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    
    logger.info('Avatar uploaded successfully', {
      avatarUrl: response.data.avatarUrl,
    });
    return response.data;
  }

  // Role Assignment endpoints
  async getUserRoleAssignments(
    tenantId: string,
    userId: string,
  ): Promise<RoleAssignment[]> {
    logger.logUserAction('Get user role assignments', { tenantId, userId });
    const response = await this.client.get<RoleAssignment[]>(
      `/api/tenants/${tenantId}/users/${userId}/role-assignments`,
    );
    logger.info('Role assignments retrieved successfully', {
      count: response.data.length,
    });
    return response.data;
  }

  async assignRoleToUser(
    tenantId: string,
    userId: string,
    request: AssignRoleRequest,
  ): Promise<RoleAssignment> {
    logger.logUserAction('Assign role to user', {
      tenantId,
      userId,
      roleId: request.roleId,
      scope: request.scope,
    });
    const response = await this.client.post<RoleAssignment>(
      `/api/tenants/${tenantId}/users/${userId}/role-assignments`,
      request,
    );
    logger.info('Role assigned successfully', {
      assignmentId: response.data.id,
    });
    return response.data;
  }

  async revokeRoleFromUser(
    tenantId: string,
    userId: string,
    assignmentId: string,
  ): Promise<void> {
    logger.logUserAction('Revoke role from user', {
      tenantId,
      userId,
      assignmentId,
    });
    await this.client.delete(
      `/api/tenants/${tenantId}/users/${userId}/role-assignments/${assignmentId}`,
    );
    logger.info('Role revoked successfully');
  }

  // SSO / OAuth Methods
  async getSsoProviders(tenantId?: string): Promise<{
    providers: Array<{
      type: string;
      provider: string;
      enabled: boolean;
    }>;
  }> {
    const params = tenantId ? { tenantId } : {};
    const response = await this.client.get('/api/auth/sso/providers', { params });
    return response.data;
  }

  // User Profile Methods
  async getUserProfile(): Promise<{
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    phoneNumber?: string;
    avatarUrl?: string;
    lastLoginAt?: string;
    role: string;
    timezone?: string;
    theme?: string;
    locale?: string;
    dateDisplayMode?: string;
  }> {
    const response = await this.client.get('/api/users/profile');
    return response.data;
  }

  async updateUserProfile(data: {
    firstName: string;
    lastName: string;
    phoneNumber?: string | null;
    timezone?: string;
    theme?: string;
    locale?: string;
    dateDisplayMode?: string;
  }): Promise<void> {
    await this.client.put('/api/users/profile', data);
  }

  /**
   * Fetches the current user's avatar image as an Object URL.
   * Uses the authenticated API client so Bearer token is sent.
   * Returns null if no avatar exists or on error.
   */
  async getAvatarBlobUrl(): Promise<string | null> {
    try {
      const response = await this.client.get('/api/users/profile/avatar', {
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  }

  /**
   * Fetches any user's avatar image as an Object URL (within the same tenant).
   * Returns null if no avatar exists or on error.
   */
  async getUserAvatarBlobUrl(tenantId: string, userId: string): Promise<string | null> {
    try {
      const response = await this.client.get(`/api/tenants/${tenantId}/users/${userId}/avatar`, {
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  }

  // API Key Management
  async getApiKeys(tenantId: string): Promise<ApiKeyDto[]> {
    const response = await this.client.get(`/api/tenants/${tenantId}/api-keys`);
    return response.data;
  }

  async getApiKey(tenantId: string, apiKeyId: string): Promise<ApiKeyDto> {
    const response = await this.client.get(`/api/tenants/${tenantId}/api-keys/${apiKeyId}`);
    return response.data;
  }

  async createApiKey(tenantId: string, request: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    const response = await this.client.post(`/api/tenants/${tenantId}/api-keys`, request);
    return response.data;
  }

  async revokeApiKey(tenantId: string, apiKeyId: string): Promise<void> {
    await this.client.post(`/api/tenants/${tenantId}/api-keys/${apiKeyId}/revoke`);
  }

  async deleteApiKey(tenantId: string, apiKeyId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/api-keys/${apiKeyId}`);
  }

  async rotateTenantApiKey(tenantId: string, apiKeyId: string): Promise<CreateApiKeyResponse> {
    const response = await this.client.post(`/api/tenants/${tenantId}/api-keys/${apiKeyId}/rotate`);
    return response.data;
  }

  async getAvailableApiRoles(tenantId: string): Promise<ApiKeyRoleDto[]> {
    const response = await this.client.get(`/api/tenants/${tenantId}/api-keys/available-roles`);
    return response.data;
  }

  // System Info
  async getSystemInfo(): Promise<import('../types').SystemInfoResponse> {
    logger.logUserAction('Get system info');
    const response = await this.client.get<import('../types').SystemInfoResponse>(
      '/api/system/info',
    );
    logger.info('System info retrieved successfully', {
      componentCount: response.data.components.length,
    });
    return response.data;
  }

  async getVersionHistory(): Promise<import('../types').VersionHistoryResponse> {
    logger.logUserAction('Get version history');
    const response = await this.client.get<import('../types').VersionHistoryResponse>(
      '/api/system/version-history',
    );
    logger.info('Version history retrieved successfully');
    return response.data;
  }

  async getWhatsNew(): Promise<import('../types').WhatsNewResponse> {
    const response = await this.client.get<import('../types').WhatsNewResponse>(
      '/api/whats-new',
    );
    return response.data;
  }

  async dismissWhatsNew(): Promise<void> {
    await this.client.post('/api/whats-new/dismiss');
  }

  async sendAssistantMessage(
    tenantId: string,
    request: { message: string; conversationId?: string },
  ): Promise<AssistantConversation> {
    const now = new Date().toISOString();
    const buildFallbackResponse = (content: string): AssistantConversation => ({
      conversationId: request.conversationId ?? now,
      messages: [
        {
          role: 'assistant',
          content,
          timestamp: now,
        },
      ],
    });

    try {
      const response = await this.client.post<{
        message: string;
        conversationId: string;
        suggestedActions?: { label: string; url: string; actionType: string }[];
        creditsCharged?: number;
        operationType?: string;
      }>(
        `/api/tenants/${tenantId}/assistant/messages`,
        request,
      );
      const data = response.data;
      if (!data || !data.message || !data.conversationId) {
        logger.warn('Assistant API returned an unexpected response shape.', {
          tenantId,
        });
        return buildFallbackResponse(
          'Beacon returned an unexpected response. Please try again shortly.',
        );
      }

      return {
        conversationId: data.conversationId,
        messages: [
          {
            role: 'assistant',
            content: data.message,
            timestamp: now,
            suggestedActions: data.suggestedActions,
            creditsCharged: data.creditsCharged,
            operationType: data.operationType,
          },
        ],
      };
    } catch (error) {
      const statusCode = axios.isAxiosError(error) ? error.response?.status : undefined;
      logger.warn('Assistant API request failed. Returning fallback response.', {
        tenantId,
        statusCode,
      });
      return buildFallbackResponse(
        'Beacon is unavailable right now. Please try again shortly.',
      );
    }
  }

  async clearAssistantConversation(tenantId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/assistant/conversation`);
  }

  async submitAssistantFeedback(
    tenantId: string,
    feedback: {
      conversationId: string;
      messageIndex: number;
      isPositive: boolean;
      comment?: string;
      userQuery?: string;
      assistantResponse?: string;
    },
  ): Promise<void> {
    await this.client.post(`/api/tenants/${tenantId}/assistant/feedback`, feedback);
  }

  async getSystemAssistantUsage(startDate?: string, endDate?: string, days?: number): Promise<TenantTokenUsageSummary[]> {
    const response = await this.client.get<TenantTokenUsageSummary[]>('/api/system-monitoring/assistant-usage', {
      params: { startDate, endDate, days },
    });
    return response.data;
  }

  async getTenantAssistantUsage(tenantId: string, startDate?: string, endDate?: string, days?: number): Promise<UserTokenUsageSummary[]> {
    const response = await this.client.get<UserTokenUsageSummary[]>(`/api/tenants/${tenantId}/assistant/usage`, {
      params: { startDate, endDate, days },
    });
    return response.data;
  }

  async getAIUsage(
    tenantId: string,
    params: {
      userId?: string;
      startDate?: string;
      endDate?: string;
      source?: string;
    },
  ): Promise<AIUsageReportDto> {
    const response = await this.client.get<AIUsageReportDto>(`/api/tenants/${tenantId}/ai/usage`, {
      params,
    });
    return response.data;
  }

  // AI Resolution Suggestion
  async getLatestResolutionSuggestion(tenantId: string, incidentId: string): Promise<ResolutionSuggestionDto | null> {
    const response = await this.client.get<ResolutionSuggestionDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/resolution-suggestion`,
    );
    return response.status === 204 ? null : response.data;
  }

  async getResolutionSuggestion(
    tenantId: string,
    incidentId: string,
    options?: GetResolutionSuggestionOptions,
  ): Promise<ResolutionSuggestionDto> {
    const additionalInstructions = options?.additionalInstructions?.trim();
    const response = await this.client.post<ResolutionSuggestionDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/resolution-suggestion`,
      additionalInstructions ? { additionalInstructions } : undefined,
      { params: { forceRegenerate: options?.forceRegenerate ? true : undefined } },
    );
    return response.data;
  }

  // AI resolution-suggestion private follow-up chat (owner-scoped, read-only)
  async startIncidentAiConversation(
    tenantId: string,
    incidentId: string,
    suggestionId: string,
  ): Promise<IncidentAiConversationDto> {
    const response = await this.client.post<IncidentAiConversationDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/ai-conversations`,
      { suggestionId },
    );
    return response.data;
  }

  async getIncidentAiConversation(
    tenantId: string,
    incidentId: string,
    conversationId: string,
  ): Promise<IncidentAiConversationDto> {
    const response = await this.client.get<IncidentAiConversationDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/ai-conversations/${conversationId}`,
    );
    return response.data;
  }

  async sendIncidentAiMessage(
    tenantId: string,
    incidentId: string,
    conversationId: string,
    payload: SendIncidentAiMessageRequest,
  ): Promise<IncidentAiConversationDto> {
    const response = await this.client.post<IncidentAiConversationDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/ai-conversations/${conversationId}/messages`,
      payload,
    );
    return response.data;
  }

  async getOwnIncidentAiChatAllowance(
    tenantId: string,
    incidentId: string,
  ): Promise<IncidentAiChatAllowanceDto> {
    const response = await this.client.get<IncidentAiChatAllowanceDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/ai-chat-allowances/me`,
    );
    return response.data;
  }

  async getIncidentAiChatAllowanceSummaries(
    tenantId: string,
    incidentId: string,
  ): Promise<IncidentAiTechnicianAllowanceSummaryDto[]> {
    const response = await this.client.get<IncidentAiTechnicianAllowanceSummaryDto[]>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/ai-chat-allowances`,
    );
    return response.data;
  }

  async resetIncidentAiChatAllowance(
    tenantId: string,
    incidentId: string,
    technicianId: string,
    payload: IncidentAiChatAllowanceResetRequest,
  ): Promise<IncidentAiChatAllowanceResetResultDto> {
    const response = await this.client.post<IncidentAiChatAllowanceResetResultDto>(
      `/api/tenants/${tenantId}/incidents/${incidentId}/ai-chat-allowances/${technicianId}/reset`,
      payload,
    );
    return response.data;
  }

  async submitSuggestionFeedback(
    tenantId: string,
    incidentId: string,
    suggestionId: string,
    feedback: ResolutionSuggestionFeedbackRequest,
  ): Promise<void> {
    await this.client.post(
      `/api/tenants/${tenantId}/incidents/${incidentId}/resolution-suggestion/${suggestionId}/feedback`,
      feedback,
    );
  }

  // AI Credits
  async getAiCreditBalance(tenantId: string): Promise<AiCreditBalanceDto> {
    const response = await this.client.get(`/api/tenants/${tenantId}/ai/credits/balance`);
    return response.data;
  }

  async getAiCreditUsage(
    tenantId: string,
    params?: { startDate?: string; endDate?: string },
  ): Promise<AiCreditUsageSummaryDto> {
    const response = await this.client.get(`/api/tenants/${tenantId}/ai/credits/usage`, { params });
    return response.data;
  }

  async updateTenantAiSettings(tenantId: string, settings: UpdateTenantAiSettingsRequest): Promise<void> {
    const payload: UpdateTenantAiSettingsRequest = {
      enableAiResolutionSuggestions: settings.enableAiResolutionSuggestions,
      enableAiBeacon: settings.enableAiBeacon,
      enableAiKnowledgeSources: settings.enableAiKnowledgeSources,
      monthlyCreditsLimit: settings.monthlyCreditsLimit,
      monthlyCostCapUsd: settings.monthlyCostCapUsd,
      monthlyCreditsOperatingLimit: settings.monthlyCreditsOperatingLimit,
      monthlyProviderCostCapOperatingLimitUsd: settings.monthlyProviderCostCapOperatingLimitUsd,
      overrideReason: settings.overrideReason,
      expectedQuotaUpdatedAtUtc: settings.expectedQuotaUpdatedAtUtc,
    };

    await this.client.put(`/api/tenants/${tenantId}/ai/settings`, payload);
  }

  async getIncidentAiChatTenantSettings(tenantId: string): Promise<IncidentAiChatTenantSettingsDto> {
    const response = await this.client.get<IncidentAiChatTenantSettingsDto>(
      `/api/tenants/${tenantId}/ai/incident-chat-settings`,
    );
    return response.data;
  }

  async updateIncidentAiChatTenantSettings(
    tenantId: string,
    payload: IncidentAiChatTenantSettingsUpdateRequest,
  ): Promise<IncidentAiChatTenantSettingsDto> {
    const response = await this.client.put<IncidentAiChatTenantSettingsDto>(
      `/api/tenants/${tenantId}/ai/incident-chat-settings`,
      payload,
    );
    return response.data;
  }

  async getIncidentAiChatTechnicianOverride(
    tenantId: string,
    userId: string,
  ): Promise<IncidentAiChatTechnicianOverrideDto> {
    const response = await this.client.get<IncidentAiChatTechnicianOverrideDto>(
      `/api/tenants/${tenantId}/ai/incident-chat-settings/technicians/${userId}`,
    );
    return response.data;
  }

  async updateIncidentAiChatTechnicianOverride(
    tenantId: string,
    userId: string,
    payload: IncidentAiChatTechnicianOverrideUpdateRequest,
  ): Promise<IncidentAiChatTechnicianOverrideDto> {
    const response = await this.client.put<IncidentAiChatTechnicianOverrideDto>(
      `/api/tenants/${tenantId}/ai/incident-chat-settings/technicians/${userId}`,
      payload,
    );
    return response.data;
  }

  // AI Knowledge Sources
  async getAiKnowledgeSources(tenantId: string): Promise<AiKnowledgeSourceDto[]> {
    const response = await this.client.get(`/api/tenants/${tenantId}/ai/knowledge-sources`);
    return response.data;
  }

  async upsertAiKnowledgeSource(
    tenantId: string,
    source: UpsertAiKnowledgeSourceRequest,
  ): Promise<AiKnowledgeSourceDto> {
    const response = await this.client.put(`/api/tenants/${tenantId}/ai/knowledge-sources`, source);
    return response.data;
  }

  // System AI Credits (admin)
  async getSystemAiCreditsOverview(): Promise<SystemAiCreditsOverviewDto> {
    const response = await this.client.get('/api/system-monitoring/ai-credits');
    return response.data;
  }

  // Notifications
  async getNotifications(
    tenantId: string,
    page = 1,
    pageSize = 20,
    excludeDismissed = false,
  ): Promise<Notification[]> {
    const response = await this.client.get<Notification[]>(`/api/tenants/${tenantId}/notifications`, {
      params: { page, pageSize, excludeDismissed },
    });
    return response.data;
  }

  async getUnreadNotificationCount(tenantId: string): Promise<number> {
    const response = await this.client.get<number>(
      `/api/tenants/${tenantId}/notifications/unread-count`,
    );
    return response.data;
  }

  async getHeaderIndicators(tenantId: string, userId: string): Promise<HeaderIndicatorsDto> {
    const response = await this.client.get<HeaderIndicatorsDto>(
      `/api/tenants/${tenantId}/header-indicators`,
      {
        params: { userId },
      },
    );
    return response.data;
  }

  async getNotificationPreferences(tenantId: string): Promise<NotificationPreferenceDto[]> {
    const response = await this.client.get<NotificationPreferenceDto[]>(
      `/api/tenants/${tenantId}/notifications/preferences`,
    );
    return response.data;
  }

  async updateNotificationPreferences(
    tenantId: string,
    request: UpdateNotificationPreferencesRequest,
  ): Promise<void> {
    await this.client.put(`/api/tenants/${tenantId}/notifications/preferences`, request);
  }

  async markNotificationRead(tenantId: string, notificationId: string): Promise<void> {
    await this.client.put(`/api/tenants/${tenantId}/notifications/${notificationId}/read`);
  }

  async markAllNotificationsRead(tenantId: string): Promise<void> {
    await this.client.put(`/api/tenants/${tenantId}/notifications/read-all`);
  }

  async dismissAllNotificationsFromPanel(tenantId: string): Promise<void> {
    await this.client.put(`/api/tenants/${tenantId}/notifications/dismiss-all`);
  }

  async deleteNotification(tenantId: string, notificationId: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/notifications/${notificationId}`);
  }

  async getPushVapidPublicKey(tenantId: string): Promise<string> {
    const response = await this.client.get<{ publicKey: string }>(
      `/api/tenants/${tenantId}/push-subscriptions/vapid-public-key`,
    );
    return response.data.publicKey;
  }

  async subscribeToPushNotifications(
    tenantId: string,
    request: PushSubscriptionRequest,
  ): Promise<void> {
    await this.client.post(`/api/tenants/${tenantId}/push-subscriptions`, request);
  }

  async unsubscribeFromPushNotifications(
    tenantId: string,
    request: PushSubscriptionDeleteRequest,
  ): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/push-subscriptions`, { data: request });
  }

  private getTenantIdFromToken(): string {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const tokenParts = token.split('.');
    if (tokenParts.length < 2) {
      throw new Error('Invalid authentication token');
    }

    const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
      tenant_id?: string;
      tenantId?: string;
    };

    const tenantId = payload.tenant_id ?? payload.tenantId;
    if (!tenantId) {
      throw new Error('Tenant context not found in authentication token');
    }

    return tenantId;
  }

  // Feedback
  async submitFeedback(data: {
    pageUrl: string;
    feedbackType: string;
    message: string;
    rating?: number;
  }): Promise<void> {
    const tenantId = this.getTenantIdFromToken();
    logger.logUserAction('Submit feedback', { tenantId, feedbackType: data.feedbackType });

    await this.client.post(
      `/api/tenants/${tenantId}/feedback`,
      data,
    );
  }

  async getFeedback(tenantId: string): Promise<FeedbackDto[]> {
    logger.logUserAction('Get feedback', { tenantId });
    const response = await this.client.get<FeedbackDto[]>(
      `/api/tenants/${tenantId}/feedback`,
    );
    return response.data;
  }

  async updateFeedbackStatus(
    tenantId: string,
    feedbackId: string,
    status: string,
  ): Promise<void> {
    logger.logUserAction('Update feedback status', { tenantId, feedbackId, status });
    await this.patch(`/api/tenants/${tenantId}/feedback/${feedbackId}/status`, { status });
  }

  async deleteFeedback(
    tenantId: string,
    feedbackIds: string[],
  ): Promise<{ deletedCount: number }> {
    logger.logUserAction('Delete feedback', { tenantId, feedbackIds });
    const response = await this.client.delete<{ deletedCount: number }>(
      `/api/tenants/${tenantId}/feedback`,
      { data: { feedbackIds } },
    );
    return response.data;
  }

  // Saved Views
  async getSavedViews(tableType: string): Promise<SavedView[]> {
    const response = await this.client.get<SavedView[]>('/api/saved-views', {
      params: { tableType },
    });
    return response.data;
  }

  async getSavedViewById(id: string): Promise<SavedView> {
    const response = await this.client.get<SavedView>(`/api/saved-views/${id}`);
    return response.data;
  }

  async createSavedView(data: CreateSavedViewDto): Promise<SavedView> {
    const response = await this.client.post<SavedView>('/api/saved-views', data);
    return response.data;
  }

  async updateSavedView(id: string, data: UpdateSavedViewDto): Promise<SavedView> {
    const response = await this.client.put<SavedView>(`/api/saved-views/${id}`, data);
    return response.data;
  }

  async deleteSavedView(id: string): Promise<void> {
    await this.client.delete(`/api/saved-views/${id}`);
  }

  async setDefaultSavedView(id: string): Promise<void> {
    try {
      await this.client.post(`/api/saved-views/${id}/set-default`);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        logger.warn('Set default saved view endpoint not available or view not found', { id });
        return;
      }

      throw error;
    }
  }

  async clearDefaultSavedView(id: string): Promise<void> {
    try {
      await this.client.delete(`/api/saved-views/${id}/set-default`);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        logger.warn('Clear default saved view endpoint not available or view not found', { id });
        return;
      }

      throw error;
    }
  }

  // Navigation Quick Access
  async getMyNavLinks(tenantId: string): Promise<SavedNavLink[]> {
    const response = await this.client.get<SavedNavLink[]>(
      `/api/tenants/${tenantId}/nav-links/my`,
    );
    return response.data;
  }

  async getTenantNavLinks(tenantId: string): Promise<SavedNavLink[]> {
    const response = await this.client.get<SavedNavLink[]>(
      `/api/tenants/${tenantId}/nav-links/tenant`,
    );
    return response.data;
  }

  async getSystemNavLinks(tenantId: string): Promise<SavedNavLink[]> {
    const response = await this.client.get<SavedNavLink[]>(
      `/api/tenants/${tenantId}/nav-links/system`,
    );
    return response.data;
  }

  async getNavLinkById(tenantId: string, id: string): Promise<SavedNavLink> {
    const response = await this.client.get<SavedNavLink>(
      `/api/tenants/${tenantId}/nav-links/${id}`,
    );
    return response.data;
  }

  async createNavLink(
    tenantId: string,
    data: CreateSavedNavLinkDto,
  ): Promise<SavedNavLink> {
    const response = await this.client.post<SavedNavLink>(
      `/api/tenants/${tenantId}/nav-links`,
      data,
    );
    return response.data;
  }

  async updateNavLink(
    tenantId: string,
    id: string,
    data: UpdateSavedNavLinkDto,
  ): Promise<SavedNavLink> {
    const response = await this.client.put<SavedNavLink>(
      `/api/tenants/${tenantId}/nav-links/${id}`,
      data,
    );
    return response.data;
  }

  async deleteNavLink(tenantId: string, id: string): Promise<void> {
    await this.client.delete(`/api/tenants/${tenantId}/nav-links/${id}`);
  }

  async reorderNavLinks(
    tenantId: string,
    scope: NavLinkScope,
    orderedIds: string[],
  ): Promise<void> {
    await this.client.put(`/api/tenants/${tenantId}/nav-links/reorder`, {
      scope,
      orderedIds,
    });
  }

  // ============================================================================
  // Parties (Phase 8 — Parties admin UI)
  // ============================================================================

  async getParties(
    tenantId: string,
    params?: PartiesTypes.GetPartiesQuery,
  ): Promise<PartiesTypes.PartySummary[]> {
    const response = await this.client.get<PartiesTypes.PartySummary[]>(
      `/api/tenants/${tenantId}/parties`,
      { params },
    );
    return response.data;
  }

  async getPartyById(
    tenantId: string,
    partyId: string,
  ): Promise<PartiesTypes.PartyDetail> {
    const response = await this.client.get<PartiesTypes.PartyDetail>(
      `/api/tenants/${tenantId}/parties/${partyId}`,
    );
    return response.data;
  }

  async getPartyAgreements(
    tenantId: string,
    partyId: string,
  ): Promise<PartiesTypes.PartyAgreementSummary[]> {
    const response = await this.client.get<PartiesTypes.PartyAgreementSummary[]>(
      `/api/tenants/${tenantId}/parties/${partyId}/agreements`,
    );
    return response.data;
  }

  async createParty(
    tenantId: string,
    data: PartiesTypes.CreatePartyRequest,
  ): Promise<PartiesTypes.PartyMutationResponse> {
    logger.logUserAction('Create party', { tenantId, name: data.name });
    const response = await this.client.post<PartiesTypes.PartyMutationResponse>(
      `/api/tenants/${tenantId}/parties`,
      data,
    );
    logger.info('Party created successfully', { partyId: response.data.id });
    return response.data;
  }

  async updateParty(
    tenantId: string,
    partyId: string,
    data: PartiesTypes.UpdatePartyRequest,
  ): Promise<PartiesTypes.PartyMutationResponse> {
    logger.logUserAction('Update party', { tenantId, partyId });
    const response = await this.client.put<PartiesTypes.PartyMutationResponse>(
      `/api/tenants/${tenantId}/parties/${partyId}`,
      data,
    );
    logger.info('Party updated successfully', { partyId: response.data.id });
    return response.data;
  }

  async addPartyRole(
    tenantId: string,
    partyId: string,
    role: PartiesTypes.PartyRoleType,
  ): Promise<PartiesTypes.PartyMutationResponse> {
    logger.logUserAction('Add party role', { tenantId, partyId, role });
    const response = await this.client.post<PartiesTypes.PartyMutationResponse>(
      `/api/tenants/${tenantId}/parties/${partyId}/roles`,
      { role },
    );
    return response.data;
  }

  async removePartyRole(
    tenantId: string,
    partyId: string,
    role: PartiesTypes.PartyRoleType,
  ): Promise<void> {
    logger.logUserAction('Remove party role', { tenantId, partyId, role });
    await this.client.delete(
      `/api/tenants/${tenantId}/parties/${partyId}/roles/${role}`,
    );
  }

  async deleteParty(tenantId: string, partyId: string): Promise<void> {
    logger.logUserAction('Delete party (soft)', { tenantId, partyId });
    await this.client.delete(`/api/tenants/${tenantId}/parties/${partyId}`);
    logger.info('Party deactivated successfully', { partyId });
  }

  async syncParties(tenantId: string): Promise<PartiesTypes.PartySyncResult> {
    logger.logUserAction('Sync parties from customers/teams', { tenantId });
    const response = await this.client.post<PartiesTypes.PartySyncResult>(
      `/api/tenants/${tenantId}/parties/sync-from-customers-and-teams`,
      {},
    );
    logger.info('Sync parties completed', { ...response.data });
    return response.data;
  }

  // ============================================================================
  // Agreements (Phase 7 — Unified Agreements config UI)
  // ============================================================================

  async getAgreements(
    tenantId: string,
    params?: AgreementsTypes.GetAgreementsQuery,
  ): Promise<AgreementsTypes.AgreementDto[]> {
    const response = await this.client.get<AgreementsTypes.AgreementDto[]>(
      `/api/tenants/${tenantId}/agreements`,
      { params },
    );
    return response.data;
  }

  async getAgreementById(
    tenantId: string,
    agreementId: string,
  ): Promise<AgreementsTypes.AgreementDto> {
    const response = await this.client.get<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}`,
    );
    return response.data;
  }

  async getOverdueAgreementReviews(
    tenantId: string,
  ): Promise<AgreementsTypes.AgreementReviewStatusDto[]> {
    const response = await this.client.get<AgreementsTypes.AgreementReviewStatusDto[]>(
      `/api/tenants/${tenantId}/agreements/overdue-reviews`,
    );
    return response.data;
  }

  async getAgreementReviewStatusReport(
    tenantId: string,
  ): Promise<AgreementsTypes.AgreementReviewStatusDto[]> {
    const response = await this.client.get<AgreementsTypes.AgreementReviewStatusDto[]>(
      `/api/tenants/${tenantId}/agreements/review-status`,
    );
    return response.data;
  }

  async createAgreement(
    tenantId: string,
    data: AgreementsTypes.CreateAgreementRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Create agreement', { tenantId, name: data.name, kind: data.kind });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements`,
      data,
    );
    logger.info('Agreement created', { agreementId: response.data.id });
    return response.data;
  }

  async addAgreementVersion(
    tenantId: string,
    agreementId: string,
    data: AgreementsTypes.AddAgreementVersionRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Add agreement version', { tenantId, agreementId });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions`,
      data,
    );
    return response.data;
  }

  async prepareAgreementDraft(
    tenantId: string,
    agreementId: string,
    validFrom?: string,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Prepare agreement draft', { tenantId, agreementId });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/prepare-draft`,
      validFrom ? { validFrom } : {},
    );
    return response.data;
  }

  async addAgreementTarget(
    tenantId: string,
    agreementId: string,
    versionId: string,
    data: AgreementsTypes.AddAgreementTargetRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Add agreement target', {
      tenantId,
      agreementId,
      versionId,
      targetType: data.targetType,
    });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/targets`,
      data,
    );
    return response.data;
  }

  async approveAgreementVersion(
    tenantId: string,
    agreementId: string,
    versionId: string,
    data: AgreementsTypes.ApproveAgreementVersionRequest = {},
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Approve agreement version', { tenantId, agreementId, versionId });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/approve`,
      data,
    );
    logger.info('Agreement version approved', { agreementId, versionId });
    return response.data;
  }

  async getAgreementReevaluationImpact(
    tenantId: string,
    agreementId: string,
    versionId: string,
  ): Promise<AgreementsTypes.AgreementReevaluationImpactDto> {
    const response = await this.client.get<AgreementsTypes.AgreementReevaluationImpactDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/reevaluation-impact`,
    );
    return response.data;
  }

  async getAgreementScheduleFallbackHealth(
    tenantId: string,
  ): Promise<AgreementsTypes.AgreementScheduleFallbackHealthItemDto[]> {
    const response = await this.client.get<AgreementsTypes.AgreementScheduleFallbackHealthItemDto[]>(
      `/api/tenants/${tenantId}/agreements/schedule-fallback-health`,
    );
    return response.data;
  }

  // --- Phase 13: Agreement editing endpoints ---

  async updateAgreement(
    tenantId: string,
    agreementId: string,
    data: AgreementsTypes.UpdateAgreementRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Update agreement', { tenantId, agreementId });
    const response = await this.client.put<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}`,
      data,
    );
    logger.info('Agreement updated', { agreementId });
    return response.data;
  }

  async updateAgreementParties(
    tenantId: string,
    agreementId: string,
    data: AgreementsTypes.UpdateAgreementPartiesRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Update agreement parties', { tenantId, agreementId });
    const response = await this.client.put<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/parties`,
      data,
    );
    logger.info('Agreement parties updated', { agreementId });
    return response.data;
  }

  async activateAgreement(
    tenantId: string,
    agreementId: string,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Activate agreement', { tenantId, agreementId });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/activate`,
      {},
    );
    logger.info('Agreement activated', { agreementId });
    return response.data;
  }

  async deactivateAgreement(
    tenantId: string,
    agreementId: string,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Deactivate agreement', { tenantId, agreementId });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/deactivate`,
      {},
    );
    logger.info('Agreement deactivated', { agreementId });
    return response.data;
  }

  async deleteAgreement(tenantId: string, agreementId: string): Promise<void> {
    logger.logUserAction('Delete agreement', { tenantId, agreementId });
    await this.client.delete(`/api/tenants/${tenantId}/agreements/${agreementId}`);
    logger.info('Agreement deleted', { agreementId });
  }

  async updateAgreementVersion(
    tenantId: string,
    agreementId: string,
    versionId: string,
    data: AgreementsTypes.UpdateAgreementVersionRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Update agreement version', { tenantId, agreementId, versionId });
    const response = await this.client.put<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}`,
      data,
    );
    logger.info('Agreement version updated', { agreementId, versionId });
    return response.data;
  }

  async setAgreementVersionRuntimeDefaults(
    tenantId: string,
    agreementId: string,
    versionId: string,
    data: AgreementsTypes.SetVersionRuntimeDefaultsRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Set agreement version runtime defaults', {
      tenantId,
      agreementId,
      versionId,
    });
    const response = await this.client.put<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/runtime-defaults`,
      data,
    );
    logger.info('Agreement version runtime defaults updated', { agreementId, versionId });
    return response.data;
  }

  async recordAgreementVersionReview(
    tenantId: string,
    agreementId: string,
    versionId: string,
    data: AgreementsTypes.RecordAgreementVersionReviewRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Record agreement version review', { tenantId, agreementId, versionId });
    const response = await this.client.post<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/review`,
      data,
    );
    logger.info('Agreement version review recorded', { agreementId, versionId });
    return response.data;
  }

  async deleteAgreementVersion(
    tenantId: string,
    agreementId: string,
    versionId: string,
  ): Promise<void> {
    logger.logUserAction('Delete agreement version', { tenantId, agreementId, versionId });
    await this.client.delete(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}`,
    );
    logger.info('Agreement version deleted', { agreementId, versionId });
  }

  async updateAgreementTarget(
    tenantId: string,
    agreementId: string,
    versionId: string,
    targetId: string,
    data: AgreementsTypes.UpdateAgreementTargetRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Update agreement target', {
      tenantId,
      agreementId,
      versionId,
      targetId,
    });
    const response = await this.client.put<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/targets/${targetId}`,
      data,
    );
    logger.info('Agreement target updated', { agreementId, versionId, targetId });
    return response.data;
  }

  async setAgreementTargetOverrides(
    tenantId: string,
    agreementId: string,
    versionId: string,
    targetId: string,
    data: AgreementsTypes.SetAgreementTargetOverridesRequest,
  ): Promise<AgreementsTypes.AgreementDto> {
    logger.logUserAction('Set agreement target overrides', {
      tenantId,
      agreementId,
      versionId,
      targetId,
    });
    const response = await this.client.put<AgreementsTypes.AgreementDto>(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/targets/${targetId}/overrides`,
      data,
    );
    logger.info('Agreement target overrides updated', { agreementId, versionId, targetId });
    return response.data;
  }

  async deleteAgreementTarget(
    tenantId: string,
    agreementId: string,
    versionId: string,
    targetId: string,
  ): Promise<void> {
    logger.logUserAction('Delete agreement target', {
      tenantId,
      agreementId,
      versionId,
      targetId,
    });
    await this.client.delete(
      `/api/tenants/${tenantId}/agreements/${agreementId}/versions/${versionId}/targets/${targetId}`,
    );
    logger.info('Agreement target deleted', { agreementId, versionId, targetId });
  }
}
export const apiClient = new ApiClient();
export default apiClient;

export const aiApi = {
  getUsage: async (
    tenantId: string,
    params: {
      userId?: string;
      startDate?: string;
      endDate?: string;
      source?: string;
    },
  ): Promise<AIUsageReportDto> => {
    return apiClient.getAIUsage(tenantId, params);
  },
  getCreditBalance: async (tenantId: string): Promise<AiCreditBalanceDto> => {
    return apiClient.getAiCreditBalance(tenantId);
  },
  getCreditUsage: async (
    tenantId: string,
    params?: { startDate?: string; endDate?: string },
  ): Promise<AiCreditUsageSummaryDto> => {
    return apiClient.getAiCreditUsage(tenantId, params);
  },
  updateSettings: async (tenantId: string, settings: UpdateTenantAiSettingsRequest): Promise<void> => {
    return apiClient.updateTenantAiSettings(tenantId, settings);
  },
  getKnowledgeSources: async (tenantId: string): Promise<AiKnowledgeSourceDto[]> => {
    return apiClient.getAiKnowledgeSources(tenantId);
  },
  upsertKnowledgeSource: async (
    tenantId: string,
    source: UpsertAiKnowledgeSourceRequest,
  ): Promise<AiKnowledgeSourceDto> => {
    return apiClient.upsertAiKnowledgeSource(tenantId, source);
  },
  getIncidentChatSettings: async (tenantId: string): Promise<IncidentAiChatTenantSettingsDto> => {
    return apiClient.getIncidentAiChatTenantSettings(tenantId);
  },
  updateIncidentChatSettings: async (
    tenantId: string,
    payload: IncidentAiChatTenantSettingsUpdateRequest,
  ): Promise<IncidentAiChatTenantSettingsDto> => {
    return apiClient.updateIncidentAiChatTenantSettings(tenantId, payload);
  },
  getIncidentChatTechnicianOverride: async (
    tenantId: string,
    userId: string,
  ): Promise<IncidentAiChatTechnicianOverrideDto> => {
    return apiClient.getIncidentAiChatTechnicianOverride(tenantId, userId);
  },
  updateIncidentChatTechnicianOverride: async (
    tenantId: string,
    userId: string,
    payload: IncidentAiChatTechnicianOverrideUpdateRequest,
  ): Promise<IncidentAiChatTechnicianOverrideDto> => {
    return apiClient.updateIncidentAiChatTechnicianOverride(tenantId, userId, payload);
  },
};

// Extend axios config to include metadata
declare module 'axios' {
  export interface AxiosRequestConfig {
    metadata?: {
      startTime: number;
      expectedStatuses?: number[];
    };
  }
}
